import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import dayjs from 'dayjs';
import 'dayjs/locale/ko';
import { CoachRow, CenterAvg, won, shortWon } from './tabData';
import { FIRST_PASS_CHURN_RATE, STABLE_RULE } from './passOrder';
import { paceTooltip } from './pace';
import { RadarChart, buildRadar, RADAR_AXES, axisScoreOf } from './CoachRadar';
import {
    PLANNED_METRICS,
    NO_CENTER_AVG,
    checkSample,
    judgementMembers,
    densityMembers,
    membersLabelOf,
    usesMonthlyScope,
    usesLessonMemberDensity,
    lessonDensityOf,
} from './coachAnalysis';
import {
    composeReport,
    GRADE_META,
    RE_REGISTRATION_RISK_LINE,
    ENDING_SOON_DAYS,
    ENDING_SOON_SESSIONS,
    ReportMetric,
    TalkingPoint,
    MemberBrief,
    isSampleShortage,
    ORDER_LIST_CAP,
} from './reportComposer';
import { MemberView } from './adapters';
import { REREGISTRATION_RATE_BY_ORDER } from './memberValue';
import { buildCoachReportFileName } from './reportFileName';
import { OrderDto, CoachClosingDto } from '../../types/opsBriefing';

dayjs.locale('ko');

/**
 * 강사 월간 성과 리포트 — A4 세로 3장.
 *
 * ─────────────────────────────────────────────────────────────
 * 인사 문서로 쓰이는 종이입니다
 * ─────────────────────────────────────────────────────────────
 * 면담 자리에 놓이고 양쪽이 서명합니다. 그래서 두 가지를 지킵니다.
 *   ① 등급과 근거가 같은 지면에 있어야 한다 — 등급만 있으면 통보가 된다
 *   ② 근거로 쓸 수 없는 숫자는 '참고'라고 표시한다 — 섞이면 부당한 평가가 된다
 *
 * ─────────────────────────────────────────────────────────────
 * 3장 구성
 * ─────────────────────────────────────────────────────────────
 *   1장  성과 요약   등급 · 실적 · 지표 표 · 프로파일
 *   2장  회원과 영업  담당 회원 · 신규기회 클로징 · 매출 구성
 *   3장  면담과 합의  지시 이행 · 대화 · 목표 · 서명
 *
 * ─────────────────────────────────────────────────────────────
 * 인쇄가 한 장만 나오던 문제
 * ─────────────────────────────────────────────────────────────
 * 예전에는 시트를 position:absolute 로 띄우고 부모가 position:fixed 였습니다.
 * 고정·절대 위치 요소는 브라우저가 첫 페이지에만 그립니다.
 * 그래서 인쇄할 때는 위치를 static 으로 되돌리고 문서 흐름을 타게 합니다.
 * 팝업 안에 중첩된 fixed 부모를 피하려고 body 로 포털을 씁니다.
 */

const PRINT_CSS = `
@media print {
    html, body {
        height: auto !important;
        overflow: visible !important;
        background: #FFFFFF !important;
    }
    /* 인쇄 대상만 남긴다. visibility 로 숨기면 자리를 차지해 빈 페이지가 생긴다 */
    body > *:not(#coach-pdf-portal) { display: none !important; }
    #coach-pdf-portal { display: block !important; }

    /* 고정/절대 위치를 풀어야 2장부터 인쇄된다 */
    #coach-pdf-overlay {
        position: static !important;
        overflow: visible !important;
        background: none !important;
        padding: 0 !important;
        display: block !important;
    }
    #coach-pdf-sheet { position: static !important; }
    #coach-pdf-toolbar { display: none !important; }

    /*
     * 🔴 **한 장은 정확히 한 장이어야 한다** (2026-09-02 대표 QA ③).
     *
     * 예전에는 min-height: 296mm 였다. min 이라 **content 가 더 크면 상자가
     * 자란다.** 297mm(A4) 를 한 톨이라도 넘기는 순간 그 페이지는 두 장에
     * 걸쳐 인쇄되고, 뒤이어 page-break-after: always 가 또 끊는다.
     * 결과가 「3장짜리 리포트가 6장, 뒤쪽은 거대한 공백」이다 — 실제로
     * 대표 화면이 그랬다.
     *
     * height 로 못 박고 넘치는 부분은 자른다. 자르는 것이 아깝지만,
     * **빈 장 세 개를 끼워 넣는 것보다 낫다.** 애초에 넘치지 않도록 2장은
     * mm 예산으로 줄 수를 거꾸로 계산한다 (P2_BODY_MM 주석).
     *
     * ⚠️ 296 을 297 로 올리지 마라. A4 가 297mm 라 같은 값을 주면 반올림
     *    한 톨에 다시 두 장이 된다. 1mm 는 그 여유다.
     */
    .pdf-page {
        width: 210mm !important;
        height: 296mm !important;
        max-height: 296mm !important;
        overflow: hidden !important;
        margin: 0 !important;
        box-shadow: none !important;
        page-break-after: always;
        break-after: page;
    }
    .pdf-page:last-child { page-break-after: auto; break-after: auto; }
    .pdf-keep { page-break-inside: avoid; break-inside: avoid; }

    @page { size: A4 portrait; margin: 0; }
}
`;

const INK = '#16202E';
const MUTED = '#77828F';
const LINE = '#DDE4EE';
const SOFT = '#F4F6F9';
const NAVY = '#1A305D';
const GOOD = '#00795F';
const BAD = '#C22D38';
const WARN = '#B45309';

const toneOf = (v: ReportMetric['verdict']) =>
    v === 'GOOD' ? GOOD : v === 'BAD' ? BAD : v === 'UNKNOWN' ? '#9AA5B2' : INK;

/**
 * 위험선 아래인 수강권 순번 — '1번째 · 2번째' 같은 목록.
 *
 * ⚠️ 숫자를 문장에 직접 적지 않습니다.
 * 예전에는 "재등록률이 51.8% 이상입니다"라고 박아 뒀는데, 그 51.8 은
 * 어느 상수와도 맞지 않는 값이었습니다(2번째 실측은 53.4). 실측이 갱신되면
 * 종이에 인쇄된 숫자만 조용히 낡습니다. passOrder.ts 가 명시적으로 금지한
 * 패턴이라, 원천에서 유도합니다.
 */
const AT_RISK_ORDER_LABELS = Object.entries(REREGISTRATION_RATE_BY_ORDER)
    .filter(([, rate]) => rate < RE_REGISTRATION_RISK_LINE)
    .map(([order]) => `${order}번째`);

/* ═══════════════════════════════════════════════════════════
   회원 표 칸 표기 — 두 표가 같은 말을 쓰게 한다
   ═══════════════════════════════════════════════════════════ */

/**
 * 만료 표기.
 *
 * ─────────────────────────────────────────────────────────────
 * 세 가지가 다 틀려 있었습니다
 * ─────────────────────────────────────────────────────────────
 *   ① '회원별 상태' 표는 이미 만료된 회원을 **`만료`** 한 단어로 뭉갰습니다.
 *      3일 지난 회원과 200일 지난 회원이 같은 글자였습니다. 3일 지난 사람은
 *      전화 한 통이면 돌아오고 200일 지난 사람은 사실상 남입니다 —
 *      면담 자리에서 이 둘을 구분하지 못하면 그 표는 쓸 데가 없습니다.
 *   ② '곧 끝나는 회원' 표는 음수 분기가 아예 없어 **`D--5`** 를 찍었습니다.
 *   ③ 두 표가 서로 다른 규칙이라, 같은 회원이 표마다 다르게 보였습니다.
 *
 * `D-` 는 남은 날, `D+` 는 지난 날입니다. 나란히 놓이면 부호로 읽힙니다.
 * (인쇄물에는 마우스오버가 없으므로 표 아래 각주로도 한 번 밝힙니다)
 */
export const expiryText = (dday: number | null | undefined): string => {
    if (dday == null) return '—';
    // 0 은 '오늘 만료'다. D-0 으로 두어 지난 것과 섞이지 않게 한다
    if (dday < 0) return `D+${Math.abs(dday)}`;
    return `D-${dday}`;
};

/** 이미 만료됐는가 — 색으로 한 번 더 갈라 준다 */
export const isExpired = (dday: number | null | undefined): boolean => dday != null && dday < 0;

/**
 * 잔여 회차 표기.
 *
 * `잔여0` 처럼 단위가 없어서 옆 칸(`1.1→1회`)과 어긋났습니다.
 * 0 회는 지워야 할 값이 아니라 **가장 급한 값**이므로 그대로 씁니다.
 */
export const remainText = (
    remain: number | null | undefined,
    /**
     * 회차 개념이 없는 수강권인가 (무제한·대관).
     * `remain` 은 등록상 999 로 오므로 "잔여 999회" 가 그대로 찍힙니다 —
     * 면담 자료에 실릴 문장이라 더 위험합니다 (sessionless.ts 주석).
     */
    sessionless = false
): string =>
    sessionless ? '잔여 무제한' : remain == null ? '잔여 —' : `잔여 ${remain}회`;

/**
 * 회원 한 명에게 걸린 금액 표기.
 *
 * ⚠️ `null` 은 0 원이 아닙니다.
 * 단가·결제액이 없는 수강권(무료권·서비스 세션·라포 도입 전 이관 건)은
 * memberValue 가 계산 자체를 포기하고 null 을 돌려줍니다. 여기서 `—` 만 찍으면
 * 원장님은 그것을 "0 원짜리 회원"으로 읽습니다. 다른 화면이 이미
 * 「금액 산정 불가」라고 말하고 있으므로(memberValue.VALUE_UNAVAILABLE_TEXT)
 * 인쇄물도 같은 말을 씁니다.
 */
export const memberValueText = (amount: number | null | undefined): string =>
    amount == null ? '산정 불가' : shortWon(amount);

/**
 * 수업 속도 — **3단 라벨** (2026-08-31 QA20, 대표 지시).
 *
 * ─────────────────────────────────────────────────────────────
 * 🔴 문턱을 새로 발명하지 않습니다 — 이미 쓰는 선을 그대로 씁니다
 * ─────────────────────────────────────────────────────────────
 * 「느려짐」의 선은 `passOrder.STABLE_RULE.paceDropPct`(−30%) 입니다.
 * 이 값은 새 숫자가 아니라 화면이 이미 세 곳에서 쓰는 그 선입니다 —
 * 단계 판정(`meetsStable` ③), 주의 사유(`WATCH_REASON_TEXT.SLOWING`),
 * 목록의 붉은 페이스 배지(`memberSignals.isPaceAlert`). 여기에 다른 숫자를
 * 적으면 **리포트에서는 「유지」인 회원이 화면에서는 붉은 배지**를 답니다.
 *
 * 「빨라짐」의 선도 이미 있습니다 — `passOrder.meetsStable` 의 `pacePickedUp`
 * (`paceDeltaPct > 0`). 「공백이 길어도 늘려서 다니는 사람은 명단에 안 올린다」는
 * 판정이 쓰는 바로 그 조건입니다.
 *
 * 그래서 선이 **비대칭**입니다(−30% / 0%). 일부러 그렇습니다: 줄어드는 것은
 * 크게 줄어야 신호이고, 느는 것은 조금만 늘어도 「돌아오는 중」이라는 사실입니다.
 * 대칭으로 맞추고 싶어지면 그건 새 문턱을 만드는 것이고, 위 세 곳이 함께 움직여야
 * 합니다.
 *
 * 비교할 이전 기록이 없으면(null) 「—」입니다. 「유지」라고 적으면 안 됩니다 —
 * 두 달 전 등록한 회원이 "그대로 다니고 있다"로 읽힙니다.
 */
export const paceLabelOf = (
    paceDeltaPct: number | null
): { text: string; color: string; bold: boolean } => {
    if (paceDeltaPct == null) return { text: '—', color: '#C9D3E0', bold: false };
    if (paceDeltaPct <= STABLE_RULE.paceDropPct) return { text: '느려짐', color: BAD, bold: true };
    if (paceDeltaPct > 0) return { text: '빨라짐', color: GOOD, bold: false };
    return { text: '유지', color: MUTED, bold: false };
};

/* ═══════════════════════════════════════════════════════════
   조판 부품
   ═══════════════════════════════════════════════════════════ */

const Page = ({
    children,
    no,
    total,
    coachName,
    monthLabel,
}: {
    children: React.ReactNode;
    no: number;
    total: number;
    coachName: string;
    monthLabel: string;
}) => (
    <div
        className="pdf-page mx-auto flex flex-col"
        style={{
            width: '210mm',
            minHeight: '296mm',
            background: '#FFFFFF',
            padding: '12mm 13mm 6mm',
            boxShadow: '0 20px 60px rgba(0,0,0,0.35)',
            color: INK,
            marginBottom: 22,
        }}
    >
        {children}
        <div
            className="flex items-end justify-between"
            style={{ marginTop: 'auto', paddingTop: 10, borderTop: `1px solid ${LINE}` }}
        >
            <span style={{ fontSize: 7, color: '#A7B0BC', lineHeight: '10px' }}>
                라포에 기록된 수업·일지·예약·결제 데이터로 자동 생성했습니다. 생성형 AI가 문장을 짓지
                않으며, 모든 문장은 실제 수치에서 유도됩니다.
                <br />
                &lsquo;참고&rsquo; 표시 지표는 회원 사정이 섞여 있어 평가 근거로 쓰지 않습니다.
            </span>
            <span style={{ fontSize: 7.5, color: '#A7B0BC', flex: 'none', marginLeft: 14 }}>
                {coachName} · {monthLabel} · {no}/{total}
            </span>
        </div>
    </div>
);

/** 장 머리 — 2·3장에 얹어 문서라는 인상을 잇는다 */
const PageHead = ({ title, sub }: { title: string; sub?: string }) => (
    <div
        className="flex items-baseline justify-between"
        style={{ borderBottom: `2px solid ${NAVY}`, paddingBottom: 6, marginBottom: 10 }}
    >
        <span style={{ fontSize: 14, fontWeight: 800, letterSpacing: '-0.02em' }}>{title}</span>
        {sub && <span style={{ fontSize: 9, color: MUTED }}>{sub}</span>}
    </div>
);

const SectionTitle = ({ no, title, sub }: { no: string; title: string; sub?: string }) => (
    <div className="flex items-baseline" style={{ gap: 7, marginBottom: 5 }}>
        {/*
          data-section-no 는 테스트가 섹션 번호를 정확히 집기 위한 표식이다.
          '두 자리 숫자 span' 으로 긁었더니 점수 막대의 '53' 같은 값이 섞였다.
        */}
        <span
            data-section-no={no}
            style={{
                fontSize: 8,
                fontWeight: 800,
                color: '#FFFFFF',
                background: NAVY,
                borderRadius: 2,
                padding: '2px 5px',
                letterSpacing: '0.04em',
            }}
        >
            {no}
        </span>
        <span style={{ fontSize: 11.5, fontWeight: 800, letterSpacing: '-0.01em' }}>{title}</span>
        {sub && <span style={{ fontSize: 8.5, color: MUTED }}>{sub}</span>}
    </div>
);

/** 대표 숫자 — 지난달 대비를 함께 */
const BigStat = ({
    label,
    value,
    unit,
    prev,
    note,
    tone,
}: {
    label: string;
    value: string;
    unit?: string;
    prev?: { delta: number; improved: boolean } | null;
    note?: string;
    tone?: string;
}) => (
    /*
     * ⚠️ **칸 밖으로 새지 않게 막는 것은 여기 한 곳입니다** (2026-08-31 QA19).
     *
     * 설명 줄(`note`)은 `height: 11` 짜리 고정 상자에 `nowrap` 없이 들어 있었습니다.
     * 「받았지만 아직 수업 안 한 금액 · 달과 무관한 오늘 잔액」처럼 긴 문장이 오면
     * 8px 글씨로도 200px 이 넘어, 다섯 칸으로 나눈 카드(한 칸 ~150px)를 **가로로
     * 뚫고 나가** 옆 칸 숫자 위에 겹쳤습니다. 인쇄물이라 스크롤로 확인할 수도 없습니다.
     *
     * 그래서 카드마다 문구 길이를 재는 대신 **부품이 구조적으로 막습니다** —
     * 상자는 넘치는 것을 잘라내고(`overflow: hidden`), 글은 칸 안에서 줄바꿈합니다.
     * 높이는 고정이 아니라 최소값이라 두 줄이 되면 행 전체가 함께 내려옵니다
     * (칸끼리 밑줄이 어긋나지 않습니다). 새 문구를 넣는 사람이 이 규칙을 몰라도
     * 카드가 깨지지 않는 것이 요점입니다.
     *
     * 이 부품은 리포트의 모든 「대표 숫자」 카드가 씁니다 — 이번 달 실적 5칸,
     * 회원 구성 4칸, 신규 기회 6칸. 한 곳만 고치면 전부 함께 막힙니다.
     */
    <div className="pdf-keep" style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
        <span
            style={{
                fontSize: 8,
                color: MUTED,
                display: 'block',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
            }}
        >
            {label}
        </span>
        <div className="flex items-baseline" style={{ gap: 2, marginTop: 4, minWidth: 0 }}>
            <span
                style={{
                    fontSize: 20,
                    fontWeight: 800,
                    color: tone ?? INK,
                    letterSpacing: '-0.045em',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                }}
            >
                {value}
            </span>
            {unit && <span style={{ fontSize: 9, color: MUTED, flex: 'none' }}>{unit}</span>}
        </div>
        <div style={{ marginTop: 2, minHeight: 11, overflow: 'hidden' }}>
            {prev ? (
                <span style={{ fontSize: 8, color: prev.improved ? GOOD : BAD, fontWeight: 700 }}>
                    {prev.delta > 0 ? '▲' : '▼'} {Math.abs(prev.delta).toLocaleString('ko-KR')}
                    <span style={{ color: '#A7B0BC', fontWeight: 400 }}> 지난달 대비</span>
                </span>
            ) : (
                <span
                    style={{
                        fontSize: 8,
                        color: '#B8C0CA',
                        display: 'block',
                        lineHeight: '11px',
                        /* 한글은 아무 데서나 끊기면 못 읽는다 — 어절 단위로 넘긴다 */
                        wordBreak: 'keep-all',
                        overflowWrap: 'anywhere',
                    }}
                >
                    {note ?? '지난달 기록 없음'}
                </span>
            )}
        </div>
    </div>
);

/**
 * 불릿 막대 — 평균 대비 위치를 한 칸에.
 *
 * 표에 숫자만 있으면 "31.7% vs 25%"를 매번 머리로 비교해야 합니다.
 * 가운데 눈금이 센터 평균이고, 막대가 오른쪽이면 잘하는 것입니다.
 * 낮을수록 좋은 지표(노쇼·응답시간)는 방향을 뒤집어, **오른쪽이 항상 좋은 쪽**입니다.
 */
const BulletBar = ({ m }: { m: ReportMetric }) => {
    const W = 74;
    if (m.value == null || m.avg == null || m.avg === 0) {
        return <div style={{ width: W, flex: 'none' }} />;
    }
    const raw = (m.value - m.avg) / m.avg;
    // 좋은 쪽이 항상 오른쪽이 되도록 방향을 맞춘다
    const score = m.higherIsBetter ? raw : -raw;
    // ±100% 를 넘으면 막대가 칸을 뚫는다. 양끝에서 멈춘다.
    const clamped = Math.max(-1, Math.min(1, score));
    const half = W / 2;
    const len = Math.abs(clamped) * half;
    const tone = toneOf(m.verdict);

    return (
        <div style={{ width: W, flex: 'none', position: 'relative', height: 9 }}>
            <div style={{ position: 'absolute', inset: 0, background: '#F1F4F9', borderRadius: 1 }} />
            <div
                style={{
                    position: 'absolute',
                    left: clamped >= 0 ? half : half - len,
                    width: Math.max(len, 1.5),
                    top: 0,
                    height: 9,
                    background: tone,
                    borderRadius: 1,
                }}
            />
            {/* 센터 평균 눈금 */}
            <div
                style={{ position: 'absolute', left: half - 0.5, top: -1, width: 1, height: 11, background: '#8A96A5' }}
            />
        </div>
    );
};

/**
 * 지표 표 — 보고서의 중심.
 * 카드로 흩어 놓으면 눈이 갈 곳을 못 찾습니다. 표는 같은 자로 잰 값이
 * 세로로 정렬돼 비교가 즉시 됩니다.
 */
const MetricTable = ({
    rows,
    canCompare,
    unmeasured,
    coach,
}: {
    rows: ReportMetric[];
    canCompare: boolean;
    /** 라포가 아직 안 재서 표에서 뺀 지표 이름 */
    unmeasured: string[];
    /** 「회원당 수업 수」 분모 각주를 적으려면 원본 행이 필요하다 */
    coach: CoachRow;
}) => (
    <div style={{ border: `1px solid ${LINE}`, borderRadius: 3, overflow: 'hidden' }}>
        <div className="flex items-center" style={{ background: SOFT, padding: '5px 9px', gap: 7 }}>
            {[
                { l: '지표', w: 0 },
                { l: '이번 달', w: 54 },
                { l: '지난달', w: 42 },
                { l: '센터 평균', w: 46 },
                { l: '평균 대비', w: 74 },
                { l: '순위', w: 38 },
                { l: '판정', w: 30 },
            ].map((h) => (
                <span
                    key={h.l}
                    style={{
                        fontSize: 7.5,
                        fontWeight: 700,
                        color: MUTED,
                        flex: h.w ? 'none' : 1,
                        width: h.w || undefined,
                        textAlign: h.w ? 'right' : 'left',
                    }}
                >
                    {h.l}
                </span>
            ))}
        </div>
        {rows.map((m, i) => (
            <div
                key={m.key}
                className="pdf-keep flex items-center"
                style={{
                    padding: '4px 9px',
                    borderTop: i === 0 ? 'none' : `1px solid ${LINE}`,
                    gap: 7,
                    background: m.verdict === 'UNKNOWN' ? '#FBFCFD' : '#FFFFFF',
                }}
            >
                {/*
                  지표 이름만 있으면 원장이 "이게 왜 중요한가"를 모른 채 숫자만 봅니다.
                  리포트의 목적은 평가가 아니라 강사와의 대화라, 뜻을 같이 적습니다.
                */}
                <span className="flex flex-col" style={{ flex: 1, minWidth: 0, gap: 1 }}>
                    <span className="flex items-center" style={{ gap: 4 }}>
                        <span style={{ fontSize: 9, fontWeight: 600 }}>{m.label}</span>
                        {!m.judgeable && <span style={{ fontSize: 6.5, color: '#B8C0CA' }}>참고</span>}
                    </span>
                    <span
                        style={{
                            fontSize: 7,
                            color: '#9AA5B2',
                            lineHeight: '9px',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                        }}
                    >
                        {m.why}
                    </span>
                </span>
                {/*
                  비율만 찍으면 표본 크기가 사라진다.
                  3명 중 1명(33%)과 300명 중 100명(33%)은 면담에서 할 말이 완전히 다르다.
                */}
                <span className="flex flex-col items-end" style={{ width: 54, flex: 'none' }}>
                    <span
                        style={{ fontSize: 9.5, fontWeight: 800, color: toneOf(m.verdict), whiteSpace: 'nowrap' }}
                    >
                        {m.value == null ? '—' : `${m.value}${m.unit}`}
                    </span>
                    {m.basis && m.basis.denominator > 0 && (
                        <span style={{ fontSize: 7, color: '#9AA5B2', whiteSpace: 'nowrap' }}>
                            {m.basis.numerator}/{m.basis.denominator}
                        </span>
                    )}
                </span>
                <span style={{ fontSize: 8.5, color: MUTED, width: 42, textAlign: 'right' }}>
                    {m.prev == null ? '—' : `${m.prev}${m.unit}`}
                </span>
                <span style={{ fontSize: 8.5, color: MUTED, width: 46, textAlign: 'right' }}>
                    {m.avg == null ? '—' : `${m.avg}${m.unit}`}
                </span>
                <BulletBar m={m} />
                <span style={{ fontSize: 8.5, color: MUTED, width: 38, textAlign: 'right' }}>
                    {m.rank == null ? '—' : `${m.rank}/${m.rankTotal}`}
                </span>
                <span
                    style={{ fontSize: 8.5, fontWeight: 800, color: toneOf(m.verdict), width: 30, textAlign: 'right' }}
                >
                    {m.verdict === 'GOOD' ? '우수' : m.verdict === 'BAD' ? '보완' : m.verdict === 'FLAT' ? '보통' : '—'}
                </span>
            </div>
        ))}
        <div className="flex items-center" style={{ padding: '4px 9px', borderTop: `1px solid ${LINE}`, background: '#FBFCFD', gap: 8 }}>
            {canCompare ? (
                <>
                    <span style={{ fontSize: 7, color: '#9AA5B2' }}>
                        &lsquo;평균 대비&rsquo; 막대는 가운데 눈금이 센터 평균입니다. 오른쪽이 잘하는 쪽이며,
                        노쇼·응답 시간처럼 낮을수록 좋은 지표도 오른쪽이 좋은 쪽으로 맞췄습니다.
                    </span>
                </>
            ) : (
                <span style={{ fontSize: 7.5, color: '#9AA5B2' }}>
                    {/* 이유는 1장 '이 리포트를 읽는 방법'에 적힌다. 여기서 원인을 단정하지 않는다 */}
                    센터 평균과 맞대볼 기준이 모자라 판정과 순위는 넣지 않았습니다.
                </span>
            )}
        </div>
        {/*
          안 재는 지표를 '—' 줄로 채워 두면 표가 고장 나 보이고, 그냥 지우면
          원장이 "원래 없는 지표"인지 "우리 센터만 안 나오는지" 구분할 수 없다.
          표에서는 빼되 이름은 밝힌다.
        */}
        {unmeasured.length > 0 && (
            <div style={{ padding: '4px 9px', borderTop: `1px solid ${LINE}`, background: '#FBFCFD' }}>
                <span style={{ fontSize: 7, color: '#9AA5B2' }}>
                    아직 집계하지 않는 지표: {unmeasured.join(' · ')} — 라포에 기록이 쌓이면 표에 자동으로 추가됩니다.
                </span>
            </div>
        )}
        {/*
          🆕 「회원당 수업 수」 분모가 **그 달에 수업한 회원**이라는 것을 밝힌다.
             노쇼도 수업 1회로 세므로 노쇼만 있는 회원은 분모에 들어간다 —
             「나왔다」가 아니라 「그 달 수업 슬롯을 소비했다」는 뜻이다.
             각주가 없으면 원장이 「안 온 회원인데 왜 분모에 있나」로 읽는다.
        */}
        {usesLessonMemberDensity(coach) && (
            <div style={{ padding: '4px 9px', borderTop: `1px solid ${LINE}`, background: '#FBFCFD' }}>
                <span style={{ fontSize: 7, color: '#9AA5B2' }}>
                    「회원당 수업 수」의 분모는 그 달에 실제로 수업한 회원 {densityMembers(coach)}명입니다.
                    수강권만 있고 한 번도 안 온 회원은 빼고 셉니다.
                    노쇼도 수업 1회로 세므로 노쇼만 있는 회원도 분모에 들어갑니다.
                </span>
            </div>
        )}
    </div>
);

/**
 * 주차별 수업 흐름.
 * 총량만 보면 "120회"인데, 늘고 있는지 줄고 있는지는 안 보입니다.
 */
const WeeklyBars = ({
    weeks,
    ranges,
}: {
    weeks: number[];
    ranges: { label: string; from: string; to: string }[];
}) => {
    const max = Math.max(...weeks, 1);
    const half = Math.ceil(weeks.length / 2);

    /*
     * 좌표계.
     *
     * ⚠️ preserveAspectRatio="none" 을 쓰면서 viewBox 를 320 으로 두면,
     * 실제 렌더 폭(약 660px)까지 가로로 2배 넘게 늘어납니다. 선은 늘어나도 되지만
     * **글자까지 같이 늘어나** 숫자가 옆으로 퍼진 채 인쇄됐습니다.
     * viewBox 를 실제 폭에 맞춰 두고 균일 배율로 그립니다.
     */
    const W = 660;
    const H = 128;
    /* 값 라벨과 최대선 라벨이 앉을 자리 */
    const PAD_T = 20;
    /* 아래쪽 — 주차 라벨 두 줄이 SVG 안에 들어간다 */
    const PAD_B = 26;
    /* 왼쪽은 눈금 라벨(최대/평균) 자리를 비워 둔다 */
    const PAD_L = 34;
    const baseY = H - PAD_B;
    const xOf = (i: number) =>
        weeks.length < 2 ? (PAD_L + W - 10) / 2 : (i / (weeks.length - 1)) * (W - PAD_L - 18) + PAD_L;
    const yOf = (n: number) => baseY - (n / max) * (baseY - PAD_T);
    const avgWeek = weeks.length ? weeks.reduce((s, n) => s + n, 0) / weeks.length : 0;
    const pointsOf = (from: number, to: number) =>
        weeks.slice(from, to).map((n, k) => `${xOf(from + k)},${yOf(n)}`);
    const linePath = `M ${pointsOf(0, weeks.length).join(' L ')}`;
    /* 후반부는 앞 점부터 이어야 선이 끊기지 않는다 */
    const latePath = `M ${pointsOf(Math.max(0, half - 1), weeks.length).join(' L ')}`;
    const areaPath = `${linePath} L ${xOf(weeks.length - 1)},${baseY} L ${xOf(0)},${baseY} Z`;
    const early = weeks.slice(0, half).reduce((s, n) => s + n, 0);
    const late = weeks.slice(half).reduce((s, n) => s + n, 0);
    const rising = late >= early;
    const first = ranges[0];
    const last = ranges[ranges.length - 1];
    const mid = ranges[half];

    /**
     * 주차 이름 — '7주 전' … '이번 주'.
     *
     * 날짜만 적어 뒀더니 "07.14" 가 몇 주 전인지 세어야 했습니다.
     * 면담에서 쓰는 말은 "3주 전부터 줄었죠"이지 "7월 14일 주부터"가 아닙니다.
     *
     * ⚠️ 여기서 다시 세지 않습니다. reportComposer 가 주차를 자를 때 이미
     *    붙여 둔 이름(weeklyRanges[].label)을 그대로 씁니다 — 두 곳에서 세면
     *    주 수가 8이 아닌 강사(기록이 짧은 신규 강사)에서 어긋납니다.
     */
    const weekLabel = (i: number) => ranges[i]?.label ?? '';

    /**
     * 지난주보다 줄어든 주.
     *
     * 첫 주는 비교 대상이 없어 판정하지 않습니다 — 0 과 비교하면
     * 무조건 '늘었다'가 되어 없는 사실이 생깁니다.
     */
    const droppedAt = (i: number) => i > 0 && weeks[i] < weeks[i - 1];
    /* 경계선 자리 — 앞 절반과 뒤 절반 사이 */
    const splitX = half > 0 && half < weeks.length ? (xOf(half - 1) + xOf(half)) / 2 : null;

    return (
        <div className="pdf-keep" style={{ border: `1px solid ${LINE}`, borderRadius: 3, padding: '10px 14px' }}>
            {/*
              막대에서 꺾은선으로 바꿨습니다.
              막대는 '그 주에 몇 회'를 읽기엔 좋지만, 이 섹션이 답해야 할 질문은
              '늘고 있나 줄고 있나'입니다. 8주치 추세는 선이 훨씬 빨리 읽힙니다.
              후반부는 진한 색으로 이어 붙여 어디서 꺾였는지 눈에 걸리게 했습니다.
            */}
            <svg
                viewBox={`0 0 ${W} ${H}`}
                width="100%"
                height={H}
                /* 균일 배율 — 'none' 이면 글자까지 가로로 늘어난다 (위 좌표계 주석 참고) */
                preserveAspectRatio="xMidYMid meet"
                style={{ overflow: 'visible', display: 'block' }}
            >
                {/*
                  눈금 두 줄.
                  최대선이 없으면 점이 얼마나 높은 건지 알 수 없고,
                  평균선이 없으면 오르내림이 의미를 갖지 않는다.
                */}
                <line
                    x1={PAD_L}
                    x2={W}
                    y1={yOf(max)}
                    y2={yOf(max)}
                    stroke="#E7EDF5"
                    strokeWidth={1}
                    strokeDasharray="2 3"
                />
                <text
                    x={PAD_L - 5}
                    y={yOf(max) + 3}
                    textAnchor="end"
                    style={{ fontSize: 7.5, fill: '#A7B0BC' }}
                >
                    최대 {max}회
                </text>
                <line
                    x1={PAD_L}
                    x2={W}
                    y1={yOf(avgWeek)}
                    y2={yOf(avgWeek)}
                    stroke="#D8E0EC"
                    strokeWidth={1}
                    strokeDasharray="4 3"
                />
                <text
                    x={PAD_L - 5}
                    y={yOf(avgWeek) + 3}
                    textAnchor="end"
                    style={{ fontSize: 7.5, fill: '#A7B0BC' }}
                >
                    평균 {Math.round(avgWeek * 10) / 10}회
                </text>
                {/* 바닥선 — 0 이 어디인지 없으면 낮은 주가 얼마나 낮은지 안 보인다 */}
                <line x1={PAD_L} x2={W} y1={baseY} y2={baseY} stroke="#E7EDF5" strokeWidth={1} />

                {/*
                  앞 4주 / 뒤 4주 경계.
                  아래 요약이 "앞 4주 19회 → 뒤 4주 17회"라고 말하는데,
                  그 경계가 그래프 어디인지 표시가 없어 눈으로 맞춰 봐야 했다.
                */}
                {splitX != null && (
                    <>
                        <line
                            x1={splitX}
                            x2={splitX}
                            y1={PAD_T - 8}
                            y2={baseY}
                            stroke="#C9D3E0"
                            strokeWidth={1}
                            strokeDasharray="3 3"
                        />
                        <text
                            x={splitX - 5}
                            y={PAD_T - 10}
                            textAnchor="end"
                            style={{ fontSize: 7, fill: '#A7B0BC' }}
                        >
                            앞 {half}주
                        </text>
                        <text
                            x={splitX + 5}
                            y={PAD_T - 10}
                            textAnchor="start"
                            style={{ fontSize: 7, fontWeight: 700, fill: NAVY }}
                        >
                            뒤 {weeks.length - half}주
                        </text>
                    </>
                )}

                <path d={areaPath} fill="rgba(26,48,93,0.07)" />
                <path
                    d={linePath}
                    fill="none"
                    stroke="#C3D0E4"
                    strokeWidth={2}
                    strokeLinejoin="round"
                    strokeLinecap="round"
                />
                <path
                    d={latePath}
                    fill="none"
                    stroke={NAVY}
                    strokeWidth={2}
                    strokeLinejoin="round"
                    strokeLinecap="round"
                />
                {weeks.map((n, i) => {
                    const dropped = droppedAt(i);
                    return (
                        <g key={ranges[i]?.label ?? i}>
                            {/*
                              줄어든 주는 점과 숫자를 붉게 잡는다.
                              어느 주에 꺾였는지가 이 섹션에서 원장이 찾는 단 하나다.
                            */}
                            <circle
                                cx={xOf(i)}
                                cy={yOf(n)}
                                r={dropped ? 3.2 : 2.6}
                                fill={dropped ? BAD : '#FFFFFF'}
                                stroke={dropped ? BAD : i >= half ? NAVY : '#C3D0E4'}
                                strokeWidth={1.6}
                            />
                            <text
                                x={xOf(i)}
                                y={yOf(n) - 8}
                                textAnchor="middle"
                                style={{
                                    fontSize: 8.5,
                                    fontWeight: 700,
                                    fill: dropped ? BAD : n === 0 ? '#C9D3E0' : INK,
                                }}
                            >
                                {dropped ? `▼${n}` : n}
                            </text>
                            {/* 주차 이름과 날짜 — SVG 안에 두어야 점 위치와 정확히 맞는다 */}
                            <text
                                x={xOf(i)}
                                y={baseY + 11}
                                textAnchor="middle"
                                style={{
                                    fontSize: 7.5,
                                    fontWeight: i === weeks.length - 1 ? 700 : 400,
                                    fill: i === weeks.length - 1 ? INK : '#8B95A5',
                                }}
                            >
                                {weekLabel(i)}
                            </text>
                            <text
                                x={xOf(i)}
                                y={baseY + 21}
                                textAnchor="middle"
                                style={{ fontSize: 7, fill: '#B4BDC9' }}
                            >
                                {ranges[i]?.from}
                            </text>
                        </g>
                    );
                })}
            </svg>
            <div
                className="flex items-center justify-between"
                style={{ marginTop: 6, borderTop: `1px solid ${LINE}`, paddingTop: 5 }}
            >
                <span style={{ fontSize: 8, color: '#5B6B7F' }}>
                    {first?.from} ~ {last?.to} ({weeks.length}주) · ▼ 는 지난주보다 줄어든 주입니다
                </span>
                <span style={{ fontSize: 9, fontWeight: 700, color: rising ? GOOD : BAD }}>
                    {rising ? '▲' : '▼'} {first?.from}~{mid ? mid.from : ''} {early}회 → {mid?.from}~
                    {last?.to} {late}회
                </span>
            </div>
        </div>
    );
};

/**
 * 동료 비교 표.
 *
 * 등급만 있으면 통보가 됩니다. "왜 B인가"에 답하려면
 * 같은 센터 강사들이 어디에 있는지가 같은 지면에 있어야 합니다.
 * 본인 행을 강조해 자기 위치를 한눈에 찾게 합니다.
 */
const PeerTable = ({ me, peers, limit }: { me: CoachRow; peers: CoachRow[]; limit: number }) => {
    const cols: { l: string; w: number; get: (c: CoachRow) => string }[] = [
        /*
         * 담당·회원당 둘 다 판정 분모를 쓴다 — 표 안에서 분모가 갈리면 안 된다.
         *
         * 🔴 머리글을 '담당'이라고 적지 않는다 (2026-09-03 대표 QA ②).
         *    이 값은 `monthlyMembers ?? activeMembers` 라 **배정된 담당 회원 수가
         *    아니다.** 브리핑 강사 카드는 같은 이름으로 배정 전체(만료 포함)를
         *    세는데, 실측에서 그 둘이 50명 대 5명이었다. 같은 이름으로 열 배
         *    차이 나는 두 수가 화면을 건너다니면 원장은 어느 쪽이 고장인지 모른다.
         */
        { l: usesMonthlyScope(me) ? '월간' : '활성', w: 38, get: (c) => `${judgementMembers(c)}명` },
        { l: '수업', w: 38, get: (c) => `${c.lessons}회` },
        {
            l: '회원당',
            w: 42,
            get: (c) => {
                const d = lessonDensityOf(c);
                return d == null ? '—' : `${d}회`;
            },
        },
        {
            /*
              🔴 **% 만 찍지 않는다.** 이 표는 동료와 나란히 놓고 읽는 자리라,
                 표본이 안 보이면 「1명 중 1명 = 100%」가 「40명 중 40명 = 100%」와
                 같은 줄에 선다.

                 칸이 42pt 라 「N명 중 M명」은 안 들어간다. **압축 표기 `M/N·X%`**
                 로 넣는다(예 `1/2·50%`). 그래도 넘치면 아래 렌더에서 두 줄로
                 접힌다 — `whiteSpace` 를 강제하지 않고 `lineHeight` 를 준 이유다.
            */
            l: '재등록',
            w: 42,
            get: (c) => {
                if (c.conversionPct == null) return '—';
                const b = c.basis?.conversion ?? null;
                return b && b.denominator > 0
                    ? `${b.numerator}/${b.denominator}·${c.conversionPct}%`
                    : `${c.conversionPct}%`;
            },
        },
        { l: '클로징', w: 42, get: (c) => (c.closingRate == null ? '—' : `${c.closingRate}%`) },
        { l: '매출', w: 58, get: (c) => (c.revenue == null ? '—' : `${shortWon(c.revenue)}`) },
    ];
    // 매출이 큰 순 — 센터 기여도 순서가 가장 자연스럽다
    const sorted = [...peers].sort((a, b) => (b.revenue ?? 0) - (a.revenue ?? 0));
    /*
     * 강사가 20명인 센터를 다 실으면 표만 106mm 라 2장이 통째로 밀린다.
     * 면담 자료에 필요한 건 전원 명부가 아니라 '내가 어디쯤인가'다.
     * 상위 N명을 싣되, 본인이 잘리면 본인 줄을 끝에 붙여 반드시 보이게 한다.
     */
    const head = sorted.slice(0, limit);
    const mineIn = head.some((c) => c.coachId === me.coachId);
    const rows = mineIn ? head : [...head.slice(0, Math.max(0, limit - 1)), ...sorted.filter((c) => c.coachId === me.coachId)];
    const hidden = sorted.length - rows.length;

    return (
        <div style={{ border: `1px solid ${LINE}`, borderRadius: 3, overflow: 'hidden' }}>
            <div className="flex items-center" style={{ background: SOFT, padding: '4px 9px', gap: 6 }}>
                <span style={{ fontSize: 7.5, fontWeight: 700, color: MUTED, flex: 1 }}>강사</span>
                {cols.map((c) => (
                    <span
                        key={c.l}
                        style={{ fontSize: 7.5, fontWeight: 700, color: MUTED, width: c.w, textAlign: 'right' }}
                    >
                        {c.l}
                    </span>
                ))}
            </div>
            {rows.map((c, i) => {
                const mine = c.coachId === me.coachId;
                return (
                    <div
                        key={c.coachId}
                        className="pdf-keep flex items-center"
                        style={{
                            padding: '3px 9px',
                            borderTop: i === 0 ? 'none' : `1px solid ${LINE}`,
                            gap: 6,
                            background: mine ? '#EEF3FB' : '#FFFFFF',
                        }}
                    >
                        <span
                            style={{ fontSize: 9, fontWeight: mine ? 800 : 500, flex: 1, color: mine ? NAVY : INK }}
                        >
                            {c.name}
                            {mine && <span style={{ fontSize: 7.5, marginLeft: 4 }}>본인</span>}
                        </span>
                        {cols.map((col) => (
                            <span
                                key={col.l}
                                style={{
                                    /* 압축해도 넘치면 두 줄로 접힌다 — 잘리는 것보다 낫다 */
                                    fontSize: col.l === '재등록' ? 8 : 8.5,
                                    lineHeight: '10px',
                                    fontWeight: mine ? 700 : 400,
                                    color: mine ? INK : MUTED,
                                    width: col.w,
                                    textAlign: 'right',
                                }}
                            >
                                {col.get(c)}
                            </span>
                        ))}
                    </div>
                );
            })}
            {hidden > 0 && (
                <div style={{ padding: '3px 9px', borderTop: `1px solid ${LINE}`, background: SOFT }}>
                    <span style={{ fontSize: 7.5, color: MUTED }}>
                        매출 상위 {rows.length}명만 실었습니다. 나머지 {hidden}명은 강사 현황에서 확인하세요.
                    </span>
                </div>
            )}
            {/*
              압축 표기의 뜻을 밝힌다. `1/2·50%` 를 설명 없이 두면 무엇이 분모인지
              읽는 사람이 정할 수 없다.
            */}
            {rows.some((c) => c.basis?.conversion != null) && (
                <div style={{ padding: '3px 9px', borderTop: `1px solid ${LINE}`, background: SOFT }}>
                    <span style={{ fontSize: 7.5, color: MUTED }}>
                        재등록 칸은 「재등록 완료 / 정상 종료 · 비율」입니다.
                    </span>
                </div>
            )}
        </div>
    );
};

/**
 * 종합 프로파일 점수표.
 *
 * ─────────────────────────────────────────────────────────────
 * 왜 도형 옆에 표를 세우나
 * ─────────────────────────────────────────────────────────────
 * 레이더든 막대든 **모양**은 "이 강사가 고른가 치우쳤나"만 답합니다.
 * 면담에서 실제로 나오는 질문은 "그래서 몇 점인데요", "센터는요",
 * "제가 몇 등인가요" 셋인데, 도형에는 그 답이 없습니다.
 * 원장이 03 지표 평가 표까지 되짚어 가서 맞춰 봐야 했습니다.
 *
 * 점수·평균·순위를 축 옆에 그대로 세웁니다.
 *   · 점수는 도형이 그리는 그 값입니다 (같은 환산식, CoachRadar.RADAR_AXES)
 *   · 센터 평균도 **같은 자로** 환산합니다 — 자가 다르면 비교가 거짓이 됩니다
 *   · 순위는 03 표가 이미 매긴 값을 그대로 가져옵니다 (ReportMetric.rank)
 *     여기서 다시 매기면 같은 지면에서 두 숫자가 어긋납니다
 */
type AxisScore = {
    label: string;
    /** 도형이 그리는 그 환산 점수 */
    score: number;
    /** 원값 — '31.7%' · '97회'. 점수만 있으면 무엇을 잰 건지 모른다 */
    rawText: string | null;
    /** 센터 평균을 같은 자로 환산한 점수. 기준이 없으면 null */
    avgScore: number | null;
    rank: number | null;
    rankTotal: number | null;
};

const axisTone = (score: number) => (score >= 60 ? GOOD : score < 55 ? BAD : '#3D73DD');

const AxisScoreTable = ({
    rows,
    /** 막대를 같이 그릴 것인가 — 레이더를 안 쓰는 경우엔 이 막대가 곧 도형이다 */
    showBar,
}: {
    rows: AxisScore[];
    showBar: boolean;
}) => (
    <div style={{ width: '100%', border: `1px solid ${LINE}`, borderRadius: 3, overflow: 'hidden' }}>
        <div className="flex items-center" style={{ background: SOFT, padding: '3px 7px', gap: 6 }}>
            <span style={{ fontSize: 6.5, fontWeight: 700, color: MUTED, flex: 1, minWidth: 0 }}>축</span>
            {showBar && <span style={{ flex: 1, minWidth: 0 }} />}
            <span style={{ fontSize: 6.5, fontWeight: 700, color: MUTED, width: 22, textAlign: 'right' }}>
                점수
            </span>
            <span style={{ fontSize: 6.5, fontWeight: 700, color: MUTED, width: 26, textAlign: 'right' }}>
                평균
            </span>
            <span style={{ fontSize: 6.5, fontWeight: 700, color: MUTED, width: 30, textAlign: 'right' }}>
                순위
            </span>
        </div>
        {rows.map((r, i) => {
            const tone = axisTone(r.score);
            return (
                <div
                    key={r.label}
                    className="flex items-center"
                    style={{ padding: '3px 7px', gap: 6, borderTop: i === 0 ? 'none' : `1px solid ${LINE}` }}
                >
                    <span className="flex items-baseline" style={{ flex: 1, minWidth: 0, gap: 3 }}>
                        <span style={{ fontSize: 8.5, fontWeight: 600, whiteSpace: 'nowrap' }}>{r.label}</span>
                        {r.rawText && (
                            <span style={{ fontSize: 7, color: '#9AA5B2', whiteSpace: 'nowrap' }}>
                                {r.rawText}
                            </span>
                        )}
                    </span>
                    {showBar && (
                        <div
                            style={{ flex: 1, height: 6, background: '#EEF2F8', borderRadius: 2, minWidth: 0, position: 'relative' }}
                        >
                            <div
                                style={{
                                    width: `${Math.max(Math.min(r.score, 100), 2)}%`,
                                    height: '100%',
                                    background: tone,
                                    borderRadius: 2,
                                }}
                            />
                            {/* 센터 평균 눈금 — 막대가 이 선을 넘었는지가 곧 판정이다 */}
                            {r.avgScore != null && (
                                <div
                                    style={{
                                        position: 'absolute',
                                        left: `${Math.max(Math.min(r.avgScore, 100), 0)}%`,
                                        top: -1,
                                        width: 1,
                                        height: 8,
                                        background: '#7C8794',
                                    }}
                                />
                            )}
                        </div>
                    )}
                    <span
                        style={{ fontSize: 9, fontWeight: 800, color: tone, width: 22, textAlign: 'right' }}
                    >
                        {Math.round(r.score)}
                    </span>
                    <span style={{ fontSize: 8, color: MUTED, width: 26, textAlign: 'right' }}>
                        {r.avgScore == null ? '—' : Math.round(r.avgScore)}
                    </span>
                    <span style={{ fontSize: 8, color: MUTED, width: 30, textAlign: 'right' }}>
                        {r.rank == null ? '—' : `${r.rank}/${r.rankTotal}`}
                    </span>
                </div>
            );
        })}
        <div style={{ padding: '3px 7px', borderTop: `1px solid ${LINE}`, background: '#FBFCFD' }}>
            <span style={{ fontSize: 6.5, color: '#9AA5B2', lineHeight: '9px' }}>
                점수는 100점 만점 환산입니다. 평균은 센터 평균을 같은 자로 환산한 값이라
                지표 표의 원값(%·회)과 숫자가 다릅니다.
                {showBar && ' 막대 위 세로선이 센터 평균 자리입니다.'}
            </span>
        </div>
    </div>
);

/** 회원 한 줄 */
/*
 * 고정폭 칸이 두 줄로 접히면 한 줄이 5.4mm → 9mm 가 된다.
 * 회원 30명인 강사에서 2장이 넘친 두 번째 원인이라 전부 nowrap 으로 못 박는다.
 */
const MemberRow = ({ m, i }: { m: MemberBrief; i: number }) => (
    <div
        className="pdf-keep flex items-center"
        style={{ padding: '4px 9px', borderTop: i === 0 ? 'none' : `1px solid ${LINE}`, gap: 7 }}
    >
        <span style={{ fontSize: 9.5, fontWeight: 700, width: 52, flex: 'none', whiteSpace: 'nowrap' }}>{m.name}</span>
        <span
            style={{
                fontSize: 7,
                color: m.passOrderLabel === '1번째' ? BAD : MUTED,
                border: `1px solid ${LINE}`,
                borderRadius: 2,
                padding: '1px 3px',
                flex: 'none', whiteSpace: 'nowrap',
            }}
        >
            {m.passOrderLabel}
        </span>
        <span style={{ fontSize: 7.5, color: '#9AA5B2', width: 56, flex: 'none', whiteSpace: 'nowrap' }}>{m.product}</span>
        {/*
          note 를 흐르게 뒀더니 긴 문장에서 3줄까지 늘어나 한 줄이 9mm 가 됐다.
          회원이 30명인 강사에서 표가 다음 장으로 흩어진 원인이다. 한 줄로 못 박는다.
        */}
        <span
            title={m.note}
            style={{
                fontSize: 8.5,
                color: '#5B6B7F',
                flex: 1,
                lineHeight: '12px',
                minWidth: 0,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
            }}
        >
            {m.note}
        </span>
        {/*
          🔴 「13→6회」에서 **3단 라벨**로 바꿨다 (2026-08-31 QA20, 대표 지시).
             종이 위에서 두 숫자와 화살표를 매 줄마다 머리로 빼는 것은 면담에
             쓸 수 있는 정보가 아니다. 원장이 필요한 것은 "이 회원 느려졌나"다.
             원 수치는 title 로 남긴다 — 사실을 지우지는 않는다.
        */}
        <span
            title={paceTooltip(m.paceBefore, m.paceNow)}
            style={{
                fontSize: 8.5,
                fontWeight: paceLabelOf(m.paceDeltaPct).bold ? 700 : 400,
                color: paceLabelOf(m.paceDeltaPct).color,
                width: 44,
                flex: 'none',
                whiteSpace: 'nowrap',
                textAlign: 'right',
            }}
        >
            {paceLabelOf(m.paceDeltaPct).text}
        </span>
        <span style={{ fontSize: 8, color: MUTED, width: 44, flex: 'none', whiteSpace: 'nowrap', textAlign: 'right' }}>
            {remainText(m.remain, m.sessionless)}
        </span>
        <span
            style={{
                fontSize: 8,
                // 이미 만료된 줄은 색으로도 갈라 준다 — D+ 를 D- 로 잘못 읽지 않도록
                color: isExpired(m.expiryDday) ? BAD : MUTED,
                fontWeight: isExpired(m.expiryDday) ? 700 : 400,
                width: 38,
                flex: 'none',
                whiteSpace: 'nowrap',
                textAlign: 'right',
            }}
        >
            {expiryText(m.expiryDday)}
        </span>
        {/*
          🔴 「순번 평균 재등록률」 칸을 뺐다 (2026-08-31 QA20, 대표 지시).

          그 숫자는 같은 순번이면 **전원이 똑같은 값**을 받습니다 — 이 회원을
          진단한 값이 아니라 코호트 평균입니다. 그런데 회원 줄에 개인 수치처럼
          서 있으니, 30줄짜리 명부에서 52.9 가 스무 번 반복되고 원장은 그걸
          사람마다 다른 값으로 읽습니다. 순번 자체는 이름 옆 배지가 이미
          말하고 있고(`passOrderLabel`), 위험 판정은 「재등록 위험 회원」 섹션이
          따로 합니다. 여기서는 뺍니다.

          ⚠️ `MemberBrief.reRegistrationRate` 는 지우지 마세요. 그 섹션과
             정렬(`reRegistrationRisk`)이 여전히 씁니다.

          남은 「걸린 금액」은 **미사용 선수금**입니다 (정책 M-D4). 옛 `valueAmount`
          에는 재등록 기대가 섞여 있었습니다 — 강사에게 건네는 종이에 "이 회원에게
          82만원이 걸려 있다"고 적혀 있는데 그중 절반이 아직 안 들어온 기댓값이면,
          강사는 그 돈을 이미 있는 돈으로 읽고 원장과 다른 숫자를 들고 면담에 앉습니다.
        */}
        <span
            style={{
                fontSize: 9,
                fontWeight: m.prepaid == null ? 400 : 700,
                color: m.prepaid == null ? '#9AA5B2' : INK,
                width: 56,
                flex: 'none',
                whiteSpace: 'nowrap',
                textAlign: 'right',
            }}
        >
            {memberValueText(m.prepaid)}
        </span>
    </div>
);

/**
 * 단계 띠 — 표를 단계마다 쪼개지 않는다.
 *
 * 단계별로 테두리 있는 블록을 따로 그렸더니 회원 1명을 보여주는 데
 * 머리글만 15mm 를 썼습니다. 네 단계면 60mm — 2장이 통째로 밀립니다.
 * 하나의 표 안에 띠 한 줄로 단계를 나누면 4mm 면 됩니다.
 */
/**
 * 회원 표 컬럼 머리글 (2026-09-01 QA27).
 *
 * ─────────────────────────────────────────────────────────────
 * 🔴 왜 필요했나
 * ─────────────────────────────────────────────────────────────
 * 값만 늘어놓고 무엇인지는 표 **밖의 산문**("칸 순서는 수업 속도·잔여·만료·
 * 걸린 금액")으로만 설명하고 있었다. 인쇄물에는 마우스오버가 없어서, 원장님은
 * 그 문장을 외운 채 눈으로 칸을 세어 맞춰야 했다. 면담 자리에서 그건 안 읽힌다.
 *
 * ─────────────────────────────────────────────────────────────
 * 🔴 폭·간격이 `MemberRow` 와 **정확히 같아야 한다**
 * ─────────────────────────────────────────────────────────────
 * 오른쪽 네 칸(44 / 44 / 38 / 56)과 `gap: 7`, 좌우 `padding: '4px 9px'` 가
 * 행과 같으면, 왼쪽을 `flex: 1` 하나로 덮어도 오른쪽 끝에서부터 정확히 같은
 * 자리에 선다(둘 다 오른쪽 블록이 210px 을 차지한다).
 * **한쪽 폭만 고치면 라벨과 값이 어긋나고, 그건 종이에서 되돌릴 수 없다.**
 *
 * ─────────────────────────────────────────────────────────────
 * 왜 단계마다 반복하지 않고 맨 위 한 번인가
 * ─────────────────────────────────────────────────────────────
 * 이 표는 **한 장 안에 들어가도록 잘려 있다** — `memberRowBudget` 이 남은 지면
 * (`roomAfterPeers`)에서 역산해 줄 수를 정하고, 넘치는 회원은 「나머지 N명」으로
 * 접힌다. 즉 표가 페이지를 넘어가는 일이 구조적으로 없으므로, 다음 장에서 라벨을
 * 잃는 경우가 생기지 않는다.
 *
 * 반대로 단계마다 반복하면 최대 4줄이 더 붙고, 그만큼 **실제 회원이 표에서
 * 밀려난다**(예산이 줄 수로 환산되므로 한 줄 = 회원 한 명). 이 파일은 같은 이유로
 * 「나머지 N명」도 단계마다 안 붙이고 표 아래 한 줄로 합쳐 뒀다.
 */
const MemberTableHead = () => {
    const label = (text: string, width: number) => (
        <span
            key={text}
            style={{
                fontSize: 7.5,
                fontWeight: 700,
                color: MUTED,
                width,
                flex: 'none',
                whiteSpace: 'nowrap',
                textAlign: 'right',
            }}
        >
            {text}
        </span>
    );
    return (
        <div
            className="flex items-center"
            style={{ background: SOFT, padding: '4px 9px', gap: 7 }}
        >
            {/* 왼쪽(이름·순번·상품·상태)은 한 칸으로 덮는다 — 순번 배지 폭이 가변이라 나눌 수 없다 */}
            <span
                style={{
                    fontSize: 7.5,
                    fontWeight: 700,
                    color: MUTED,
                    flex: 1,
                    minWidth: 0,
                    whiteSpace: 'nowrap',
                }}
            >
                회원 · 상품 · 상태
            </span>
            {label('수업 속도', 44)}
            {label('잔여', 44)}
            {label('만료', 38)}
            {label('걸린 금액', 56)}
        </div>
    );
};

const StageBand = ({ title, tone, count, hint }: { title: string; tone: string; count: number; hint?: string }) => (
    <div
        className="flex items-baseline"
        style={{ background: '#F7F9FC', borderTop: `1px solid ${LINE}`, borderLeft: `2px solid ${tone}`, padding: '2px 9px', gap: 6 }}
    >
        <span style={{ fontSize: 8.5, fontWeight: 800, color: tone, flex: 'none' }}>
            {title} {count}명
        </span>
        {hint && (
            <span
                style={{
                    fontSize: 7.5,
                    color: '#9AA5B2',
                    flex: 1,
                    minWidth: 0,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                }}
            >
                {hint}
            </span>
        )}
    </div>
);

const StageBlock = ({
    title,
    tone,
    rows,
    hint,
    limit,
}: {
    title: string;
    tone: string;
    rows: MemberBrief[];
    hint?: string;
    limit?: number;
}) => {
    if (!rows.length) return null;
    // 지면이 넘치면 표가 다음 장으로 흩어진다. 급한 순서로 자르고 몇 명 남았는지 밝힌다.
    const shown = limit == null ? rows : rows.slice(0, limit);
    const hidden = rows.length - shown.length;
    return (
        <>
            <StageBand title={title} tone={tone} count={rows.length} hint={hint} />
            {shown.map((m, i) => (
                <MemberRow key={m.memberId} m={m} i={i + 1} />
            ))}
            {/* 단계마다 '나머지 N명'을 붙이면 그 줄만 20mm 다. 표 아래 한 줄로 합친다 */}
        </>
    );
};

/** 면담 대화 — 그대로 읽고 답을 받아 적는다 */
const TalkCard = ({ p, i }: { p: TalkingPoint; i: number }) => {
    const tone = p.kind === 'PRAISE' ? GOOD : p.kind === 'ASK' ? BAD : NAVY;
    const label =
        p.kind === 'PRAISE' ? '잘하고 있는 것' : p.kind === 'ASK' ? '함께 볼 부분' : '같이 정할 것';
    return (
        <div
            className="pdf-keep"
            style={{
                border: `1px solid ${LINE}`,
                borderLeft: `3px solid ${tone}`,
                borderRadius: 3,
                padding: '8px 12px',
                marginBottom: 6,
            }}
        >
            <div className="flex items-baseline" style={{ gap: 6 }}>
                <span style={{ fontSize: 7.5, fontWeight: 800, color: tone, flex: 'none' }}>
                    {String(i + 1).padStart(2, '0')} {label}
                </span>
                <span style={{ fontSize: 10, fontWeight: 700 }}>{p.headline}</span>
            </div>
            <p style={{ fontSize: 10.5, marginTop: 4, lineHeight: '15px' }}>&ldquo;{p.question}&rdquo;</p>
            <span style={{ fontSize: 8.5, color: MUTED, marginTop: 3, display: 'block', lineHeight: '12px' }}>
                근거 · {p.basis}
            </span>
            <div style={{ marginTop: 5, borderTop: `1px dashed ${LINE}`, paddingTop: 4 }}>
                <span style={{ fontSize: 7.5, color: '#A7B0BC' }}>강사 답변</span>
                <div style={{ borderBottom: `1px solid ${LINE}`, height: 12, marginTop: 2 }} />
            </div>
        </div>
    );
};

/* ═══════════════════════════════════════════════════════════
   본체
   ═══════════════════════════════════════════════════════════ */

type Props = {
    coach: CoachRow;
    month?: string;
    centerAvg?: CenterAvg;
    peers?: CoachRow[];
    members?: MemberView[];
    orders?: OrderDto[];
    closing?: CoachClosingDto | null;
    centerName?: string;
    /**
     * 집계 기준 — 서버 CoachesResponse.period / asOf 를 그대로 넘깁니다.
     * 언제 기준인지 모르는 숫자는 근거가 못 됩니다. 특히 진행 중인 달은
     * '중간 집계'라고 밝혀야 월말 실적으로 오해하지 않습니다.
     */
    period?: { start: string; end: string } | null;
    asOf?: string | null;
    /**
     * 🆕 `portfolioValue`(담당 미사용 선수금)를 언제 기준으로 잰 값인가 (ISO).
     *
     * 그 값만 선택한 달을 안 따라갑니다. 예전에는 「(오늘)」이라고만 적었는데,
     * 어제 뽑은 PDF 와 오늘 화면이 다른 이유를 그 라벨로는 설명할 수 없습니다.
     * 서버가 주면 실제 시각을 적습니다. 안 주면(구버전) 예전대로 「(오늘)」입니다.
     */
    portfolioValueAsOf?: string | null;
    onClose: () => void;
};

/** 한 장에 담을 수 있는 회원 수 — 넘치면 표가 다음 장으로 흩어진다 */
/**
 * 2장에 실을 회원 줄 수 상한.
 *
 * 브라우저에서 실제로 재서 정한 값입니다. A4 한 장에 쓸 수 있는 본문은 267.8mm 이고,
 * 2장의 나머지 섹션이 그중 대부분을 씁니다. 회원 줄이 하나 늘 때마다 약 5.5mm 를
 * 먹는데, 한도를 넘으면 표가 4장째로 흘러 "3장짜리 문서"가 깨집니다.
 *
 * 단계별로 따로 자르지 않고 **급한 순서로 전체 10줄**을 채웁니다.
 * 단계마다 상한을 두면 이탈 위험 12명인 강사도 6명만 보이게 됩니다.
 */
/**
 * ── 2장 지면 예산 (mm) ──────────────────────────────────────────
 *
 * 상수로 "9명"이라고 못 박아 뒀더니, 담당 30명 · 센터 강사 20명인 강사에서
 * 2장이 **130mm** 넘쳐 4·5장으로 흩어졌습니다. 줄 수가 아니라 **높이**가
 * 문제라서, 줄 수는 남은 지면에서 거꾸로 계산합니다.
 *
 * 아래 mm 값은 브라우저에서 실제로 잰 것입니다. 눈대중이 아닙니다.
 * A4 296mm − 상하 패딩 18mm − 푸터 8.2mm = 269.8mm 가 본문 한도입니다.
 */
const P2_BODY_MM = 269.8;
/** 머리글 7.1 + 06 회원 구성 32 + 07 재등록 위험 33 + 섹션 간격 5.3×4 */
const P2_FIXED_MM = 7.1 + 32 + 33 + 5.3 * 4 + 15; // +15 = 섹션 사이 여백 실측 보정
const ENDING_HEAD_MM = 24.3;
const ENDING_ROW_MM = 5.9;
const STAGE_HEAD_MM = 4.2;
const MEMBER_ROW_MM = 6.1;
/**
 * 회원 표 컬럼 머리글 한 줄 (2026-09-01 QA27).
 *
 * ⚠️ **지면 예산에서 반드시 빼야 한다.** 안 빼면 표가 딱 한 줄만큼 넘쳐서
 *    마지막 회원이 다음 장으로 흘러가고, 그 한 줄 때문에 장이 하나 더 생긴다.
 *    이 파일의 예산 계산은 전부 실측 mm 기반이라, 새 줄을 그리면 반드시
 *    그 값도 함께 넣어야 한다(단계 띠가 `STAGE_HEAD_MM` 로 그렇게 하고 있다).
 */
const MEMBER_HEAD_MM = 4.2;
const PEER_HEAD_MM = 18;
const PEER_ROW_MM = 5.3;
/** 동료 비교표에 실을 강사 수 — 면담에 필요한 건 전원 명부가 아니라 '내 위치'다 */
const PEER_LIMIT = 6;
/** 회원 표는 최소 이만큼은 보인다. 이보다 적으면 실을 이유가 없다 */
const MEMBER_ROWS_MIN = 5;
const ENDING_ROWS_MIN = 3;

/* ── 3장 지면 예산 ─────────────────────────────────────────────
 *
 * 🔴 이 파일의 장은 `height: 296mm; overflow: hidden` 이다(PRINT_CSS 주석).
 *    넘치면 다음 장으로 흐르는 것이 아니라 **잘린다.** 3장 맨 아래가 서명란이라,
 *    한 블록을 새로 얹으면 원장·강사 서명 줄이 종이에서 사라진다.
 *    그래서 3장에 무언가를 더할 때는 **반드시 같은 높이를 어디선가 뺀다.**
 */

/** 3장에 실을 지시 건수 — 넘치면 서명란이 잘린다 */
const ORDER_LIMIT = 6;
/** 3장에 실을 대화 카드 수 — 한 장에 3개가 한계다 */
const TALK_LIMIT = 3;

const CoachPdfReport = ({
    coach,
    month,
    centerAvg,
    peers,
    members,
    orders,
    closing,
    centerName,
    period,
    asOf,
    portfolioValueAsOf,
    onClose,
}: Props) => {
    /**
     * 이 리포트가 아직 화면에 있는가.
     *
     * 🔴 폰트 대기(최대 3초) 중에 언마운트되면 `window.print()` 를 **부르지 않는다**
     *    (2026-09-03 대표 제보). 리포트가 사라진 뒤에 인쇄를 열면 인쇄용
     *    `<style>` 도 함께 걷힌 상태라 **브리핑 화면이 그대로 인쇄된다.**
     *    아무것도 안 나오는 것보다 남의 화면이 종이에 찍히는 게 나쁘다.
     */
    const alive = useRef(true);

    useEffect(() => {
        alive.current = true;
        const el = document.createElement('style');
        el.innerHTML = PRINT_CSS;
        document.head.appendChild(el);
        return () => {
            alive.current = false;
            document.head.removeChild(el);
        };
    }, []);

    /** 인쇄 대화상자를 여는 중인가 — 연타로 두 번 열리는 것을 막는다 */
    const [printing, setPrinting] = useState(false);

    /**
     * 이 리포트가 다루는 달 (`YYYY-MM`).
     *
     * ⚠️ 인쇄 파일명과 지면의 달 표기가 **같은 값**을 써야 해서 여기서 한 번만
     *    정한다. 파일명 쪽에서 `month ?? 오늘` 을 다시 쓰면, 자정을 넘기는
     *    순간 종이에 찍힌 달과 파일 이름의 달이 갈린다.
     */
    const monthKey = month ?? dayjs().format('YYYY-MM');

    /**
     * 인쇄하는 동안 잠시 바꿔 둔 탭 제목의 **원래 값**.
     * `null` 이면 지금 바꿔 둔 것이 없다는 뜻이다.
     */
    const prevTitleRef = useRef<string | null>(null);

    /**
     * 탭 제목을 원래대로 돌린다.
     *
     * 🔴 **두 번 불려도 안전해야 한다.** `afterprint` 와 언마운트가 겹칠 수 있고,
     *    그때 두 번째 호출이 「파일명」을 원래 값으로 착각해 그대로 굳히면
     *    탭 제목이 영영 리포트 이름으로 남는다.
     */
    const restoreTitle = useCallback(() => {
        if (prevTitleRef.current === null) return;
        document.title = prevTitleRef.current;
        prevTitleRef.current = null;
    }, []);

    /*
     * 언마운트 안전망.
     *
     * `afterprint` 가 오기 전에 리포트를 닫는 경로가 있다(인쇄 대화상자를 열지
     * 못한 브라우저, 대화상자를 띄운 채 화면을 이동). 그때 제목을 안 돌리면
     * **원장님 탭 제목이 강사 이름으로 남는다.**
     */
    useEffect(
        () => () => {
            window.removeEventListener('afterprint', restoreTitle);
            restoreTitle();
        },
        [restoreTitle]
    );

    /*
     * ─────────────────────────────────────────────────────────────
     * 🔴 인쇄는 **레이아웃이 확정된 뒤에** 연다 (2026-09-02 대표 QA ①)
     * ─────────────────────────────────────────────────────────────
     * 예전에는 버튼이 곧바로 `window.print()` 를 불렀다. 그런데 이 리포트는
     * 지면을 **mm 단위로 계산해서** 줄 수를 정한다(P2_BODY_MM). 웹폰트가
     * 아직 안 붙은 상태에서 인쇄를 열면 글자 폭·줄높이가 대체 폰트 기준이라
     * 계산과 실제가 어긋나고, 그만큼 지면이 넘쳐 장이 더 생긴다.
     *
     * 그래서 두 가지를 기다린다.
     *   ① `document.fonts.ready` — 웹폰트가 실제로 적용될 때까지
     *   ② 다음 프레임 — 인쇄용 CSS(고정/절대 해제)가 반영된 레이아웃
     *
     * ⚠️ **실패해도 인쇄는 연다.** 폰트 API 가 없거나 늦어도 「다운로드가
     *    아예 안 된다」가 되면 안 된다 — 기다림은 품질이지 관문이 아니다.
     */
    const openPrintDialog = async () => {
        if (printing) return;
        setPrinting(true);
        try {
            await Promise.race([
                document.fonts?.ready ?? Promise.resolve(),
                /* 폰트가 영영 안 끝나는 환경에서도 3초면 연다 */
                new Promise((r) => setTimeout(r, 3000)),
            ]);
            await new Promise((r) => requestAnimationFrame(() => r(null)));
        } catch {
            /* 무시하고 연다 */
        } finally {
            setPrinting(false);
            /*
             * ⚠️ 기다리는 사이에 리포트가 닫혔으면 **열지 않는다.**
             *    "실패해도 인쇄는 연다"는 폰트가 늦을 때의 이야기지,
             *    인쇄할 리포트 자체가 사라진 경우가 아니다. 이때 열면
             *    가려 줄 인쇄 CSS 도 이미 걷혀서 브리핑 화면이 인쇄된다.
             */
            if (alive.current) {
                /*
                 * ─────────────────────────────────────────────────────
                 * 🔴 저장 파일명은 `document.title` 로만 정할 수 있다
                 * ─────────────────────────────────────────────────────
                 * 브라우저 인쇄 대화상자는 탭 제목을 파일명으로 가져간다.
                 * 그대로 두면 누구의 몇 월 리포트든 전부
                 * `Rappo Manager - 헬스 & 피트니스 관리.pdf` 로 떨어진다.
                 *
                 * 제목은 **대화상자를 열기 직전에** 바꾸고 `afterprint` 로
                 * 되돌린다. 안 되돌리면 인쇄가 끝난 뒤에도 원장님 탭이
                 * 강사 이름을 달고 있다.
                 *
                 * 이미 바꿔 둔 상태면 다시 담지 않는다 — 그러면 "원래 값" 자리에
                 * 파일명이 들어가 영영 못 돌아온다(인쇄가 비동기인 브라우저에서
                 * 버튼을 두 번 누르면 실제로 그렇게 된다).
                 */
                if (prevTitleRef.current === null) prevTitleRef.current = document.title;
                document.title = buildCoachReportFileName({
                    coachName: coach.name,
                    month: monthKey,
                    centerName,
                });
                window.addEventListener('afterprint', restoreTitle, { once: true });
                try {
                    window.print();
                } catch {
                    /* 대화상자를 못 열었으면 `afterprint` 도 안 온다 — 여기서 되돌린다 */
                    restoreTitle();
                }
            }
        }
    };

    /*
     * 비교 기준선.
     * ⚠️ 이건 인쇄물이다. 목 상수로 메우면 지어낸 기준으로 매긴 S/A/B/C 가
     * 종이에 찍혀 강사 앞에 놓인다. 되돌릴 방법이 없다.
     * 서버 평균이 없으면 전부 null 로 떨어뜨려, 지표 표는 '센터 평균 —',
     * 판정은 '—', 등급은 'NA' 로 나가게 한다.
     */
    const avg = centerAvg ?? NO_CENTER_AVG;
    const plan = composeReport(coach, avg, peers ?? [coach], { members, orders });
    const book = plan.memberBook;
    const record = plan.orderRecord;
    const radar = buildRadar(coach);
    /* monthKey 는 인쇄 파일명과 공유하므로 위(openPrintDialog 앞)에서 정한다 */
    const monthLabel = dayjs(`${monthKey}-01`).format('YYYY년 M월');

    /*
     * 집계 기준.
     * 서버가 period 를 안 보내면 달의 처음~끝으로 적되, 지어내지는 않는다.
     * 진행 중인 달은 '중간 집계'라고 반드시 밝힌다 — 월말 실적으로 읽히면
     * 강사 평가가 통째로 틀어진다.
     */
    const monthStart = dayjs(`${monthKey}-01`);
    const isRunningMonth = monthStart.isSame(dayjs(), 'month');
    const rangeStart = period?.start ?? monthStart.format('YYYY-MM-DD');
    const rangeEnd =
        period?.end ??
        (isRunningMonth ? dayjs().format('YYYY-MM-DD') : monthStart.endOf('month').format('YYYY-MM-DD'));
    const periodLabel = `${dayjs(rangeStart).format('YY.MM.DD')} ~ ${dayjs(rangeEnd).format('YY.MM.DD')}${
        isRunningMonth ? ' (중간)' : ''
    }`;
    const asOfLabel = (asOf ? dayjs(asOf) : dayjs()).format('YY.MM.DD HH:mm');
    /*
     * 선수금·담당 회원 수의 기준시각 꼬리표.
     *
     * 서버가 `portfolioValueAsOf` 를 주면 **실제 시각**을 적는다. 「(오늘)」은
     * 지난달 리포트를 오늘 열었을 때만 참이고, 어제 뽑아 둔 PDF 에서는 거짓이다.
     * ⚠️ 근사치임을 아는 채로 쓴다 — 서버가 과거 시점의 잔액을 재구성할 수는
     *    없다(`status` 는 이력이 아니라 현재 필드다). 밝히는 것이 유일한 정직이다.
     */
    const asOfSuffix = portfolioValueAsOf
        ? `(${dayjs(portfolioValueAsOf).format('M/D HH:mm')} 기준)`
        : '(오늘)';
    const grade = GRADE_META[plan.grade];

    const deltaOf = (now: number | null, prev: number | null | undefined, higherIsBetter = true) => {
        if (now == null || prev == null) return null;
        const delta = now - prev;
        if (delta === 0) return null;
        return { delta, improved: higherIsBetter ? delta > 0 : delta < 0 };
    };
    const revenueDelta = deltaOf(coach.revenue, coach.prev?.revenue);
    const lessonsDelta = deltaOf(coach.lessons, coach.prev?.lessons);

    /*
     * 회원 표 배분.
     * 급한 단계부터 자리를 채우고, 남은 자리를 다음 단계에 넘긴다.
     * 이렇게 해야 이탈 위험이 12명인 강사도 12명이 다 보이고,
     * 위험이 없는 강사는 그 자리를 안정 궤도 회원이 채운다.
     */
    const hasPeerTable = (peers ?? []).length > 1;

    /*
     * 남은 지면을 나눠 쓴다.
     *   동료 비교표 → '곧 끝나는 회원' → '회원별 상태' 순으로 자리를 준다.
     * 회원이 적은 강사는 한도에 안 걸려 전원이 그대로 보인다.
     */
    const peerShown = hasPeerTable ? Math.min((peers ?? []).length, PEER_LIMIT) : 0;
    const peerMm = hasPeerTable ? PEER_HEAD_MM + peerShown * PEER_ROW_MM : 0;

    const endingTotal = book?.endingSoon.length ?? 0;
    const roomAfterPeers = P2_BODY_MM - P2_FIXED_MM - peerMm;
    /* '곧 끝나는 회원'은 다음 달 매출이 걸린 명단이라 남은 지면의 절반을 먼저 가져간다 */
    const endingLimit =
        endingTotal === 0
            ? 0
            : Math.max(
                  ENDING_ROWS_MIN,
                  Math.min(
                      endingTotal,
                      Math.floor((roomAfterPeers * 0.5 - ENDING_HEAD_MM) / ENDING_ROW_MM)
                  )
              );
    const endingMm = endingLimit > 0 ? ENDING_HEAD_MM + endingLimit * ENDING_ROW_MM : 0;

    /* 단계 제목은 회원이 있는 단계에만 붙는다. 없는 단계 몫까지 빼면 지면이 남는다 */
    const stageCount = book
        ? (['AT_RISK', 'GOLDEN', 'FIRST_PASS', 'STABLE'] as const).filter(
              (k) => book.byStage[k].length > 0
          ).length
        : 0;
    const memberRowBudget = Math.max(
        MEMBER_ROWS_MIN,
        Math.floor(
            (roomAfterPeers - endingMm - MEMBER_HEAD_MM - stageCount * STAGE_HEAD_MM) /
                MEMBER_ROW_MM
        )
    );

    const memberBlocks = (() => {
        const spec = [
            { key: 'AT_RISK', title: '이탈 위험', tone: BAD, hint: '오던 사람이 안 오고 있습니다. 결제보다 안부가 먼저입니다' },
            { key: 'GOLDEN', title: '재등록 골든타임', tone: WARN, hint: '수강권이 끝나기 전이 마지막 기회입니다' },
            { key: 'FIRST_PASS', title: '첫 수강권', tone: GOOD, hint: '이탈률이 가장 높은 구간입니다' },
            { key: 'STABLE', title: '안정 궤도', tone: '#3D73DD', hint: '따로 챙기지 않아도 되는 회원입니다' },
        ] as const;

        const filled = spec.map((sp) => ({ ...sp, rows: book ? book.byStage[sp.key] : [] }));

        /*
         * 급한 순서로 그냥 채웠더니, 이탈 위험 8명이 지면을 다 먹고
         * 골든타임·첫 수강권·안정 궤도가 전부 "지면이 모자라"로 떨어졌다.
         * 면담에서 세 단계를 통째로 못 보는 리포트는 쓸모가 없다.
         *
         * 그래서 두 번 돈다.
         *   1) 회원이 있는 단계에 최소 한 줄씩 먼저 준다 — 모든 단계가 보인다.
         *   2) 남은 줄을 급한 순서로 더 준다.
         * 안정 궤도는 '따로 챙기지 않아도 되는 회원'이라 마지막에 둔다.
         */
        const quota: number[] = filled.map((f) => (f.rows.length > 0 ? 1 : 0));
        let left = memberRowBudget - quota.reduce((a2, b2) => a2 + b2, 0);
        filled.forEach((f, i) => {
            if (left <= 0) return;
            const more = Math.min(f.rows.length - quota[i], left);
            if (more > 0) {
                quota[i] += more;
                left -= more;
            }
        });
        return filled.map((f, i) => ({ ...f, limit: Math.min(f.rows.length, quota[i]) }));
    })();

    /** 표에 못 실은 회원 수 — 사라진 게 아니라 '지면이 모자란 것'이라고 밝혀야 한다 */
    const memberHidden = memberBlocks.reduce((sum, b) => sum + (b.rows.length - b.limit), 0);

    const hasClosing = !!closing && closing.leadMembers > 0;
    /*
     * 2장(회원·영업)에 실을 게 하나도 없는 센터가 있다.
     * 이제 막 도입해서 회원 배정도, 신규기회도, 비교할 동료도 없는 경우다.
     * 그때 빈 장을 인쇄하면 기능이 고장 난 것처럼 보인다. 아예 뺀다.
     */
    const hasPage2 = (!!book && book.total > 0) || hasClosing || hasPeerTable;

    /*
     * 지표 표 정리.
     *
     * 열 개를 세워 뒀는데 라포가 실제로 재는 건 여섯 개였습니다.
     * 나머지 넷(예약 응답 속도·당일 취소·회원 일지 열람률·담당 회원 부상 기록)은
     * 서버가 늘 null 을 보냅니다 — 집계 경로가 아직 없습니다.
     * 목 데이터에만 값이 있어서 화면에서는 멀쩡해 보였습니다.
     *
     * '—' 로 줄을 채우면 표가 고장 나 보이고, 판정 근거로도 못 씁니다.
     * 표에서 빼고 이름만 각주로 남깁니다.
     */
    const shownMetrics = plan.metrics.filter((m) => m.value != null);
    /*
     * 각주는 두 가지를 합쳐 보여준다.
     *   · 라포가 아직 안 재는 지표 (PLANNED_METRICS — 모든 센터 공통)
     *   · 이 강사만 표본이 없어 못 잰 지표
     * 둘을 구분 안 하면 원장이 "우리 센터만 안 나오나"를 알 수 없다.
     */
    const unmeasuredMetrics = [
        ...PLANNED_METRICS.map((m) => m.label),
        ...plan.metrics.filter((m) => m.value == null).map((m) => m.label),
    ];

    const measured = radar.filter((r): r is { label: string; score: number } => r.score != null);
    /* 레이더는 축이 다섯 개는 돼야 도형으로 읽힌다. 그 아래는 막대가 정직하다 */
    const useRadar = measured.length >= 5;

    /*
     * 축을 왜 뺐는지 **이유별로** 센다 (2026-09-03 대표 QA ③).
     *
     * 「집계 전」과 「표본 부족」은 다른 말이다. 뭉뚱그려 '집계 전'이라고 적으면
     * 원장은 서버를 기다리면 채워질 값이라고 읽는데, 실제로는 수업을 더 해야
     * 채워지는 값이다. 면담에서 강사에게 해 줄 말이 정반대가 된다.
     */
    const sample = checkSample(coach);
    const droppedForSample = sample.enough
        ? 0
        : RADAR_AXES.filter((a) => a.needsSample && a.normalize(a.rawOf(coach)) != null).length;
    const droppedForMissing = radar.length - measured.length - droppedForSample;

    /**
     * 축별 점수·평균·순위.
     *
     * 평균과 순위는 03 지표 평가 표가 이미 계산해 둔 것을 그대로 가져온다.
     * 여기서 다시 매기면 같은 지면에서 두 숫자가 어긋난다.
     * (수업 진행량은 센터 평균이 없다 — readAllMetrics.avgOf 가 null 을 준다.
     *  그러면 '평균 —' 로 나가고 없는 기준을 지어내지 않는다.)
     */
    const metricByKey = new Map(plan.metrics.map((m) => [m.key, m]));
    const axisScores = RADAR_AXES.map((axis) => {
        /* 점수는 계산기(axisScoreOf)가 낸다 — 여기서 환산식을 다시 적지 않는다 */
        const score = axisScoreOf(axis, coach);
        if (score == null) return null;
        const metric = metricByKey.get(axis.metricKey);
        return {
            label: axis.label,
            score,
            /*
             * 원값에 이름을 붙인다 — 축 이름과 방향이 반대인 축이 있다.
             * '출석관리' 옆의 '0%' 는 노쇼율이라, 이름 없이 적으면
             * 「출석관리 0% · 100점」이라는 앞뒤 안 맞는 줄이 인쇄된다.
             */
            rawText:
                metric?.value == null
                    ? null
                    : `${axis.rawLabel ? `${axis.rawLabel} ` : ''}${metric.value}${metric.unit}`,
            avgScore: axis.normalize(metric?.avg),
            rank: metric?.rank ?? null,
            rankTotal: metric?.rankTotal ?? null,
        };
    }).filter((r): r is NonNullable<typeof r> => r != null);
    /*
     * 프로파일 근거 문장 — 보완을 먼저, 남으면 강점으로 채운다.
     * 보완이 급하니 앞에 두되, 보완이 없는 강사도 칸이 비지 않게 한다.
     */
    const profileReasons = [...plan.gaps, ...plan.strengths].slice(0, 3);
    const strongAxes = measured.filter((r) => r.score >= 60).sort((a, b) => b.score - a.score);
    const weakAxes = measured.filter((r) => r.score < 55).sort((a, b) => a.score - b.score);

    const TOTAL = hasPage2 ? 3 : 2;

    /*
     * 섹션 번호는 구성이 달라지면 손으로 못 맞춘다.
     * 손으로 적으면 04 가 두 번 나오거나 05 가 통째로 비는데,
     * 인쇄물에서 번호가 비면 한 장이 빠진 줄 안다.
     */
    let seq = 0;
    const no = () => {
        seq += 1;
        return String(seq).padStart(2, '0');
    };

    const body = (
        <div
            id="coach-pdf-overlay"
            className="fixed inset-0 overflow-y-auto"
            style={{ background: 'rgba(12,22,40,0.68)', zIndex: 3000, padding: '26px 0' }}
            /*
             * 🔴 포털 밖으로 클릭을 흘려보내지 않는다 (2026-09-03 대표 제보).
             *
             * 이 본문은 `document.body` 로 포털을 쓰지만, **React 합성 이벤트는
             * DOM 트리가 아니라 React 트리를 타고 오른다.** 그래서 리포트를 여는
             * 팝업의 배경이 `onClick={onClose}` 를 달고 있으면, 리포트 안의
             * 「PDF 다운로드」를 누른 클릭이 거기까지 올라가 **팝업 전체가 닫혔다.**
             * 그 뒤 폰트 대기가 끝나고 인쇄가 열리면 리포트는 이미 없다.
             *
             * ⚠️ 이 방어가 못 막는 것: `onMouseDown`·`onPointerDown`·키보드로
             *    닫는 부모. 지금 이 저장소의 팝업은 전부 `onClick` 배경이고
             *    Esc 를 쓰는 곳이 없다(OrderConfirmModal 주석 참고). 그래도
             *    부모 쪽에서도 중첩을 풀어 두 겹으로 막는다 (CoachReportModal).
             */
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
            role="presentation"
        >
            <div
                id="coach-pdf-toolbar"
                className="mx-auto mb-[12px] flex items-center justify-between"
                style={{ width: '210mm' }}
            >
                <span style={{ color: '#FFFFFF', fontSize: 13.5 }}>
                    A4 세로 {TOTAL}장 · 인쇄 대화상자에서 &quot;대상: PDF로 저장&quot;을 선택하세요
                </span>
                <div className="flex items-center" style={{ gap: 8 }}>
                    <button
                        type="button"
                        onClick={openPrintDialog}
                        disabled={printing}
                        className="rounded-[9px]"
                        style={{
                            height: 40,
                            padding: '0 20px',
                            background: '#FFFFFF',
                            border: 'none',
                            color: NAVY,
                            fontSize: 15,
                            fontWeight: 700,
                            cursor: 'pointer',
                        }}
                    >
                        {printing ? '준비 중…' : '🖨 PDF 다운로드'}
                    </button>
                    <button
                        type="button"
                        onClick={onClose}
                        className="rounded-[9px]"
                        style={{
                            height: 40,
                            padding: '0 16px',
                            background: 'rgba(255,255,255,0.16)',
                            border: '1px solid rgba(255,255,255,0.3)',
                            color: '#FFFFFF',
                            fontSize: 15,
                            cursor: 'pointer',
                        }}
                    >
                        닫기
                    </button>
                </div>
            </div>

            <div id="coach-pdf-sheet">
                {/* ══════════ 1장 · 성과 요약 ══════════ */}
                <Page no={1} total={TOTAL} coachName={coach.name} monthLabel={monthLabel}>
                    {/* 표지 밴드 */}
                    <div
                        style={{
                            background: NAVY,
                            margin: '-12mm -13mm 0',
                            padding: '11mm 13mm 8mm',
                            color: '#FFFFFF',
                        }}
                    >
                        <span style={{ fontSize: 8, letterSpacing: '0.24em', color: '#9DB4D8', fontWeight: 700 }}>
                            RAPPO MONTHLY COACH PERFORMANCE REPORT
                        </span>
                        <div className="flex items-end justify-between" style={{ marginTop: 8 }}>
                            <div>
                                <span style={{ fontSize: 26, fontWeight: 800, letterSpacing: '-0.04em' }}>
                                    {coach.name} 강사
                                </span>
                                <span style={{ fontSize: 15, fontWeight: 600, color: '#C6D6EE', marginLeft: 9 }}>
                                    {monthLabel}
                                </span>
                            </div>
                            {/* 등급 — 인사 문서의 첫 줄 */}
                            <div className="flex items-end" style={{ gap: 10 }}>
                                <div style={{ textAlign: 'right' }}>
                                    <span style={{ fontSize: 7.5, color: '#8FA5C4', display: 'block' }}>
                                        종합 등급
                                    </span>
                                    {/*
                                      표본이 모자라 접은 경우에는 그렇다고 못 박는다.
                                      GRADE_META.NA 의 문구는 '비교할 기준이 모자라'로 일부러
                                      뭉뚱그려 뒀다 — NA 는 센터 평균이 아직 없을 때도 나오고,
                                      그때 '표본 부족'이라 쓰면 원장이 있지도 않은 문제(회원 수)를
                                      붙들기 때문이다. 이유가 실제로 표본일 때만 그 말을 쓴다.
                                    */}
                                    <span style={{ fontSize: 9, color: '#C6D6EE' }}>
                                        {isSampleShortage(plan.maturity)
                                            ? '표본 부족으로 평가 유보'
                                            : grade.desc}
                                    </span>
                                </div>
                                <div
                                    className="flex items-center justify-center"
                                    style={{
                                        width: 46,
                                        height: 46,
                                        borderRadius: 8,
                                        background: 'rgba(255,255,255,0.14)',
                                        border: '1px solid rgba(255,255,255,0.28)',
                                    }}
                                >
                                    <span style={{ fontSize: 25, fontWeight: 800, letterSpacing: '-0.04em' }}>
                                        {grade.label}
                                    </span>
                                </div>
                            </div>
                        </div>
                        <div
                            className="flex"
                            style={{
                                marginTop: 11,
                                gap: 22,
                                borderTop: '1px solid rgba(255,255,255,0.16)',
                                paddingTop: 9,
                            }}
                        >
                            {[
                                /* 빈 문자열도 '없음'이다 — `??` 는 ''를 통과시켜 칸이 비어 보인다 */
                                { l: '센터', v: centerName?.trim() ? centerName : '—' },
                                /*
                                  🔴 「오늘」을 라벨에 박는다 (2026-08-31 QA18).
                                     이 세 값은 선택한 달과 **무관하다.** 서버가
                                     `buildPortfolios(centerId)` 로 뽑는데 그 함수는
                                     month 인자를 아예 받지 않고, 모집단이
                                     `status:'active'` — 즉 지금 이 순간의 상태다.
                                     7월 리포트를 8월에 열어도 8월 담당 회원이 찍힌다.

                                  ⚠️ 하필 바로 옆 칸이 「집계 기간 26.07.01 ~ 26.07.31」이다.
                                     라벨이 없으면 이 숫자도 7월 것으로 읽힌다.
                                     과거 시점 재구성은 서버에서도 불가능하다
                                     (`status` 는 이력이 아니라 현재 필드다) —
                                     그러니 고칠 수 없는 값이고, 밝히는 것이 유일한 정직이다.
                                */
                                /*
                                  🔴 「담당 회원」이 아니라 「활성 회원」이다 (2026-09-03 대표 QA ②).
                                     서버의 `members` 는 `buildPortfolios` 가 뽑는
                                     **활성 수강권 보유자**라, 배정된 담당 회원 수가 아니다
                                     (지금 `members === activeMembers` 로 같은 값이 온다).
                                     브리핑 강사 카드의 「담당 N명」은 배정 전체(만료 포함)라
                                     실측에서 50명 대 5명으로 갈렸다. 셋 다 '담당'이라 부르는 한
                                     원장은 이 리포트가 회원을 놓쳤다고 읽는다.

                                     ⚠️ 값도 `activeMembers` 로 읽는다. 라벨이 가리키는 바로 그
                                        필드를 읽어야, 백엔드가 `members` 를 진짜 담당 수로
                                        분리해 보내는 날(핸드오프 §6) 이 칸이 조용히 틀리지 않는다.
                                */
                                {
                                    /*
                                      🆕 「수업 회원」을 함께 적는다.
                                         「월간 담당 46명인데 수업 회원 31명」이 보여야
                                         회원당 수업 수의 분모가 어디서 왔는지 설명이 된다.
                                    */
                                    l: `활성 회원 ${asOfSuffix}`,
                                    v: usesMonthlyScope(coach) || usesLessonMemberDensity(coach)
                                        ? `${coach.activeMembers}명 (${membersLabelOf(coach)} ${judgementMembers(coach)}` +
                                          (usesLessonMemberDensity(coach)
                                              ? ` · 수업 회원 ${densityMembers(coach)})`
                                              : ')')
                                        : `${coach.activeMembers}명`,
                                },
                                { l: `첫 수강권 ${asOfSuffix}`, v: `${coach.firstPassMembers}명` },
                                { l: '수업량 순위', v: `${coach.lessonRank}위` },
                                { l: '집계 기간', v: periodLabel },
                                { l: '생성 시각', v: asOfLabel },
                            ].map((x) => (
                                <div key={x.l}>
                                    <span style={{ fontSize: 7.5, color: '#8FA5C4', display: 'block' }}>{x.l}</span>
                                    <span style={{ fontSize: 10.5, fontWeight: 700 }}>{x.v}</span>
                                </div>
                            ))}
                        </div>
                    </div>

                    {plan.maturity.level !== 'RICH' && (
                        <div
                            className="pdf-keep"
                            style={{
                                marginTop: 9,
                                border: '1px solid #E3D5A8',
                                background: '#FDF9EC',
                                borderRadius: 3,
                                padding: '6px 11px',
                            }}
                        >
                            {/*
                              제목을 따로 세웠더니 이 블록만 8.8mm 라 표본 부족 센터에서
                              1장이 넘쳤다. 제목과 본문을 한 줄에 붙인다.
                            */}
                            <p style={{ fontSize: 9, color: '#5F4E18', lineHeight: '13px' }}>
                                <span style={{ fontWeight: 800, color: '#8A6D1F' }}>이 리포트를 읽는 방법 </span>
                                {plan.maturity.note}
                            </p>
                        </div>
                    )}

                    <div style={{ marginTop: 10 }}>
                        <SectionTitle no={no()} title="총평" />
                        <div
                            className="pdf-keep"
                            style={{ background: SOFT, borderLeft: `3px solid ${NAVY}`, padding: '11px 13px', borderRadius: 3 }}
                        >
                            <p style={{ fontSize: 12, lineHeight: '18px', fontWeight: 700 }}>{plan.headline}</p>
                            {(plan.strengths.length > 0 || plan.gaps.length > 0) && (
                                <p style={{ fontSize: 9.5, color: '#5B6B7F', marginTop: 5, lineHeight: '14px' }}>
                                    {plan.strengths.length > 0 &&
                                        `강점 ${plan.strengths.map((m) => m.label).join(' · ')}`}
                                    {plan.strengths.length > 0 && plan.gaps.length > 0 && '   |   '}
                                    {plan.gaps.length > 0 && `보완 ${plan.gaps.map((m) => m.label).join(' · ')}`}
                                </p>
                            )}
                        </div>
                    </div>

                    <div style={{ marginTop: 10 }}>
                        <SectionTitle no={no()} title="이번 달 실적" sub={`${periodLabel} · 지난달과 나란히`} />
                        <div
                            className="flex"
                            style={{
                                border: `1px solid ${LINE}`,
                                borderRadius: 3,
                                padding: '11px 14px',
                                gap: 14,
                            }}
                        >
                            <BigStat
                                label="매출"
                                value={coach.revenue == null ? '—' : won(coach.revenue)}
                                unit={coach.revenue == null ? undefined : '원'}
                                prev={revenueDelta}
                            />
                            <BigStat
                                label="신규 결제"
                                value={coach.newRegistrations == null ? '—' : String(coach.newRegistrations)}
                                unit={coach.newRegistrations == null ? undefined : '명'}
                                note="처음 등록한 회원"
                            />
                            <BigStat
                                label="재등록 결제"
                                value={coach.reRegistrations == null ? '—' : String(coach.reRegistrations)}
                                unit={coach.reRegistrations == null ? undefined : '명'}
                                note="다시 등록한 회원"
                            />
                            <BigStat label="진행 수업" value={String(coach.lessons)} unit="회" prev={lessonsDelta} />
                            {/*
                              🔴 이 칸만 「이번 달 실적」이 아니다 (QA18).
                                 잔여 회차 × 단가인데, 잔여를 세는 기준시각이
                                 `loadUsedSessions(ids)` 의 기본값 — **호출한 그 순간**이다
                                 (`opsMemberData.service.ts` 의 `anchorTime = new Date()`).
                                 모집단도 지금 활성인 수강권이다.

                                 그래서 7월 리포트를 8월 말에 열면
                                   · 7월엔 살아 있었지만 지금 만료된 수강권 → 빠지고
                                   · 8월에 새로 판 수강권 → 들어오고
                                   · 8월에 한 수업 → 잔여를 깎는다.
                                 금액인데 섹션 라벨과 시점이 어긋나므로 반드시 밝힌다.
                            */}
                            {/*
                              문구를 짧게 줄였다 (2026-08-31 QA19). 이 칸이 다섯 칸 중
                              하나라 긴 문장은 카드 밖으로 새어 나갔다. 「달과 무관한
                              오늘 잔액」이라는 사실은 지우면 안 되므로(QA18) 아래
                              각주 줄로 옮겼다 — 거기는 행 전체 너비를 쓴다.
                            */}
                            <BigStat
                                label={`미사용 선수금 ${asOfSuffix}`}
                                value={won(coach.portfolioValue)}
                                unit="원"
                                note="받았지만 아직 수업 안 한 금액"
                            />
                        </div>
                        <span style={{ fontSize: 8, color: '#9AA5B2', marginTop: 4, display: 'block', lineHeight: '11px' }}>
                            「미사용 선수금」만 이 달의 값이 아닙니다 — 지금 활성인 수강권의 잔액이라
                            {portfolioValueAsOf
                                ? ` 위에 적힌 ${dayjs(portfolioValueAsOf).format('M월 D일 HH:mm')} 기준이고, 다시 열면 그때 기준으로 다시 계산됩니다.`
                                : ' 지난달 리포트를 나중에 열어도 오늘 기준으로 다시 계산됩니다.'}{' '}
                            나머지 칸은 {periodLabel} 기록입니다.
                        </span>

                    </div>

                    {plan.maturity.level !== 'NEW' && (
                        <div style={{ marginTop: 10 }}>
                            <SectionTitle
                                no={no()}
                                title="지표 평가"
                                sub={`${periodLabel} 기준 · ${
                                    plan.maturity.canCompare ? '센터 평균·순위 대비' : '관찰값'
                                }`}
                            />
                            <MetricTable
                                rows={shownMetrics}
                                canCompare={plan.maturity.canCompare}
                                unmeasured={unmeasuredMetrics}
                                coach={coach}
                            />
                        </div>
                    )}

                    {/*
                      이제 막 도입한 센터는 지표가 전부 비어 있다.
                      "데이터 없음"만 적어 놓으면 기능이 쓸모없어 보인다.
                      무엇을 하면 무엇이 보이는지를 적어야 다음 달에 채워진다.
                    */}
                    {plan.maturity.level === 'NEW' && (
                        <div style={{ marginTop: 10 }}>
                            <SectionTitle
                                no={no()}
                                title="이 리포트가 채워지려면"
                                sub="라포에 무엇이 쌓여야 하는가"
                            />
                            <div style={{ border: `1px solid ${LINE}`, borderRadius: 3, overflow: 'hidden' }}>
                                {[
                                    {
                                        do: '회원을 강사에게 배정하고 수강권을 등록',
                                        get: '담당 회원 명부 · 미사용 선수금 · 잔여 회차',
                                        when: '등록 즉시',
                                    },
                                    {
                                        do: '수업을 라포 일정으로 잡고 출석 체크',
                                        get: '수업량 · 주차별 흐름 · 노쇼율 · 소진 페이스',
                                        when: '2주 후부터',
                                    },
                                    {
                                        do: '수강권 등록 시 신규/재등록을 분류',
                                        get: '수강권 순번 · 재등록 확률 (이 기능의 가장 강한 근거)',
                                        when: '등록 즉시',
                                    },
                                    {
                                        do: '체험·OT를 신규기회로 기록',
                                        get: '클로징률 · 유형별 성사율 · 평균 성사 시점',
                                        when: '결제 확인 시',
                                    },
                                    {
                                        do: '수업 후 일지 작성',
                                        get: '일지 작성률 · 회원 열람률',
                                        when: '작성 즉시',
                                    },
                                ].map((r, i) => (
                                    <div
                                        key={r.do}
                                        className="pdf-keep flex items-start"
                                        style={{
                                            padding: '6px 10px',
                                            borderTop: i === 0 ? 'none' : `1px solid ${LINE}`,
                                            gap: 9,
                                        }}
                                    >
                                        <span
                                            style={{
                                                fontSize: 7.5, fontWeight: 800, color: '#FFFFFF', background: NAVY,
                                                borderRadius: 2, padding: '1px 4px', flex: 'none', marginTop: 1,
                                            }}
                                        >
                                            {i + 1}
                                        </span>
                                        <span style={{ fontSize: 9.5, fontWeight: 600, width: 150, flex: 'none', lineHeight: '13px' }}>
                                            {r.do}
                                        </span>
                                        <span style={{ fontSize: 9, color: '#5B6B7F', flex: 1, lineHeight: '13px' }}>
                                            → {r.get}
                                        </span>
                                        <span style={{ fontSize: 8, color: '#A7B0BC', width: 52, flex: 'none', textAlign: 'right' }}>
                                            {r.when}
                                        </span>
                                    </div>
                                ))}
                            </div>
                            <span style={{ fontSize: 8, color: '#9AA5B2', marginTop: 4, display: 'block', lineHeight: '11px' }}>
                                한 달치가 쌓이면 지난달과 비교할 수 있고, 담당 회원 5명부터 센터 평균과 비교합니다.
                            </span>
                        </div>
                    )}

                    {book && book.weeklyLessons.length > 0 && (
                        <div style={{ marginTop: 10 }}>
                            {/* 이 섹션만 최근 8주다. 위 표는 월 단위라 기간이 다르다 — 안 적으면 같은 기간으로 읽는다 */}
                            <SectionTitle
                                no={no()}
                                title="주차별 수업 흐름"
                                sub="최근 8주 · 담당 회원 수업 합계"
                            />
                            <WeeklyBars weeks={book.weeklyLessons} ranges={book.weeklyRanges} />
                        </div>
                    )}

                    {plan.maturity.level !== 'NEW' && (
                    <div style={{ marginTop: 10 }}>
                        <SectionTitle
                            no={no()}
                            title="종합 프로파일 · 강점과 보완"
                            sub={
                                useRadar
                                    ? '바깥 테두리가 100점 · 축마다 점수·센터 평균·순위'
                                    : '축마다 점수·센터 평균·순위'
                            }
                        />
                        <div
                            className="pdf-keep flex items-center"
                            style={{ gap: 10, border: `1px solid ${LINE}`, borderRadius: 3, padding: '8px 14px' }}
                        >
                            {/*
                              축이 다섯 개 미만이면 레이더를 쓰지 않는다.
                              네 축짜리 레이더는 마름모가 되는데, 면적이 축을 어떤 순서로
                              놓느냐에 따라 달라져 '넓을수록 우수'가 성립하지 않는다.
                              같은 값을 가로 막대로 보여주면 왜곡 없이 그대로 읽힌다.
                              서버가 응답속도·약속이행 집계를 붙여 축이 늘면 레이더로 돌아온다.
                            */}
                            {/*
                              도형은 '고른가 치우쳤나'만 답한다.
                              '몇 점인가 · 센터는 · 몇 등인가'는 옆의 점수표가 답한다.
                              레이더를 쓸 때만 도형을 따로 세우고, 막대일 때는
                              점수표의 막대가 곧 도형이라 한 번만 그린다.
                            */}
                            {useRadar && (
                                <div className="flex justify-center" style={{ width: 205, flex: 'none' }}>
                                    <RadarChart values={radar} size={112} showScores />
                                </div>
                            )}
                            <div style={{ width: useRadar ? 196 : 306, flex: 'none' }}>
                                <AxisScoreTable rows={axisScores} showBar={!useRadar} />
                            </div>
                            <div className="flex flex-1 flex-col" style={{ gap: 8, minWidth: 0 }}>
                                {/* 점수는 그래프 축에 이미 찍혀 있다. 여기서는 이름만 묶어 준다 */}
                                {strongAxes.length > 0 && (
                                    <span style={{ fontSize: 9.5 }}>
                                        <b style={{ color: GOOD }}>▲ 강점</b>{' '}
                                        {strongAxes.map((r) => r.label).join(' · ')}
                                    </span>
                                )}
                                {weakAxes.length > 0 && (
                                    <span style={{ fontSize: 9.5 }}>
                                        <b style={{ color: BAD }}>▼ 보완</b>{' '}
                                        {weakAxes.map((r) => r.label).join(' · ')}
                                    </span>
                                )}
                                {/*
                                  보완만 문장으로 뽑고 강점은 이름만 나열했었다.
                                  약점 없는 강사는 이 칸이 통째로 비고, 면담에서 칭찬할 때
                                  "무엇이 어떻게 좋은지" 댈 근거가 없다. 둘 다 문장으로 뽑는다.
                                */}
                                {profileReasons.map((m) => (
                                    <div key={m.key}>
                                        <span style={{ fontSize: 9, fontWeight: 700 }}>
                                            {m.label}
                                            {m.rank != null && (
                                                <span style={{ fontSize: 8, color: MUTED, fontWeight: 400 }}>
                                                    {' '}
                                                    센터 {m.rankTotal}명 중 {m.rank}위
                                                </span>
                                            )}
                                        </span>
                                        <p style={{ fontSize: 9.5, color: '#5B6B7F', marginTop: 1, lineHeight: '13px' }}>
                                            {m.reading}
                                        </p>
                                    </div>
                                ))}
                                {droppedForSample > 0 && (
                                    <span style={{ fontSize: 8, color: '#9AA5B2', lineHeight: '11px' }}>
                                        ※ {droppedForSample}개 축은 표본이 모자라 뺐습니다({sample.reason}).
                                        0점이 아닙니다.
                                    </span>
                                )}
                                {droppedForMissing > 0 && (
                                    <span style={{ fontSize: 8, color: '#9AA5B2', lineHeight: '11px' }}>
                                        ※ {droppedForMissing}개 축은 집계 전이라 그래프에서 뺐습니다.
                                        0점이 아닙니다.
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>
                    )}
                </Page>

                {/* ══════════ 2장 · 회원과 영업 (실을 게 있을 때만) ══════════ */}
                {hasPage2 && (
                <Page no={2} total={TOTAL} coachName={coach.name} monthLabel={monthLabel}>
                    <PageHead title="담당 회원과 영업 실적" sub={`${monthLabel} 기준`} />

                    {book && book.total > 0 ? (
                        <>
                            <div>
                                <SectionTitle no={no()} title="회원 구성" sub="지금 이 강사가 들고 있는 것" />
                                <div
                                    className="flex"
                                    style={{ border: `1px solid ${LINE}`, borderRadius: 3, padding: '11px 14px', gap: 14 }}
                                >
                                    <BigStat
                                        label="지금 챙겨야 할 회원"
                                        value={String(book.priority.length)}
                                        unit="명"
                                        note="이탈 위험 + 재등록 골든타임"
                                        tone={book.priority.length ? BAD : INK}
                                    />
                                    <BigStat
                                        label="미사용 선수금"
                                        value={won(book.unusedPrepaid)}
                                        unit="원"
                                        note="이미 받았지만 아직 수업 안 한 금액"
                                    />
                                    <BigStat
                                        label="다음 달 재등록 예상"
                                        value={won(book.expectedReRegistrationRevenue)}
                                        unit="원"
                                        note="곧 끝나는 회원의 기댓값"
                                        tone={GOOD}
                                    />
                                    <BigStat
                                        label="첫 수강권 회원"
                                        value={String(book.byStage.FIRST_PASS.length)}
                                        unit="명"
                                        // 숫자를 문자열에 박지 않는다 — 원천은 CHURN_RATE_BY_ORDER 하나다
                                        note={`이탈률 ${FIRST_PASS_CHURN_RATE}% 구간`}
                                    />
                                </div>
                                <span style={{ fontSize: 8, color: '#9AA5B2', marginTop: 4, display: 'block', lineHeight: '11px' }}>
                                    미사용 선수금은 회계상 부채입니다. 회원이 안 나오면 환불 요구가 되고, 다 쓰면
                                    재등록 기회가 됩니다. 재등록 예상액은 확률을 곱한 기댓값이라 확정 매출이
                                    아닙니다.
                                    {/*
                                      🔴 「예상액을 어디서 가져왔나」를 종이에 적는다 (정책 M5).
                                         화면에는 마우스오버로 붙였지만 인쇄물에는 그럴 자리가 없다.
                                         이걸 안 적으면 강사가 그 금액을 시세나 추정 단가로 읽는다.
                                    */}
                                    {' '}다음 수강권 예상액은 직전과 같은 금액으로 보수적으로 잡았습니다.
                                    {book.unpriced > 0 &&
                                        ` 단가가 없는 수강권 ${book.unpriced}명은 합계에서 뺐습니다.`}
                                </span>
                            </div>

                            {/*
                              비어 있어도 섹션을 지우지 않는다.
                              사라지면 읽는 사람은 "이 기능이 없다"고 생각하지
                              "확인해보니 해당자가 없다"고 생각하지 않는다.
                            */}
                            <div style={{ marginTop: 10 }}>
                                    <SectionTitle
                                        no={no()}
                                        title="재등록 위험 회원"
                                        /*
                                          "안 이어질 사람들"은 개인을 단정하는 말이다.
                                          근거는 순번 코호트 평균이므로 구간으로 부른다.
                                        */
                                        sub={`순번 평균 재등록률이 ${RE_REGISTRATION_RISK_LINE}% 미만인 구간의 회원`}
                                    />
                                    {book.reRegistrationRisk.length === 0 ? (
                                        <div
                                            className="pdf-keep"
                                            style={{ border: `1px solid ${LINE}`, borderRadius: 3, padding: '9px 12px' }}
                                        >
                                            <span style={{ fontSize: 10, color: '#5B6B7F' }}>
                                                재등록 확률이 {RE_REGISTRATION_RISK_LINE}% 미만인 회원이 없습니다.
                                            </span>
                                            <span style={{ fontSize: 8.5, color: '#9AA5B2', display: 'block', marginTop: 3, lineHeight: '12px' }}>
                                                담당 회원 전원이 순번 평균 재등록률{' '}
                                                {RE_REGISTRATION_RISK_LINE}% 이상 구간에 있습니다.
                                                {AT_RISK_ORDER_LABELS.length > 0 &&
                                                    ` ${AT_RISK_ORDER_LABELS.join(' · ')} 수강권 회원이 배정되면 여기에 표시됩니다.`}
                                            </span>
                                        </div>
                                    ) : (
                                    <div
                                        className="pdf-keep"
                                        style={{
                                            border: `1px solid ${LINE}`,
                                            borderLeft: `2px solid ${BAD}`,
                                            borderRadius: 3,
                                            padding: '9px 12px',
                                        }}
                                    >
                                        <div className="flex items-baseline" style={{ gap: 8, marginBottom: 5 }}>
                                            <span style={{ fontSize: 10.5, fontWeight: 800, color: BAD }}>
                                                {book.reRegistrationRisk.length}명
                                            </span>
                                            <span style={{ fontSize: 9, color: '#5B6B7F' }}>
                                                걸린 금액 {won(book.reRegistrationRiskValue)}원
                                            </span>
                                        </div>
                                        <div className="flex" style={{ gap: 5, flexWrap: 'wrap' }}>
                                            {book.reRegistrationRisk.slice(0, 8).map((m) => (
                                                <span
                                                    key={m.memberId}
                                                    style={{
                                                        fontSize: 9,
                                                        border: `1px solid ${LINE}`,
                                                        borderRadius: 3,
                                                        padding: '2px 6px',
                                                    }}
                                                >
                                                    {m.name}
                                                    <span style={{ color: BAD, fontWeight: 700 }}>
                                                        {' '}
                                                        {m.reRegistrationRate}%
                                                    </span>
                                                    <span style={{ color: '#A7B0BC' }}> {m.passOrderLabel}</span>
                                                </span>
                                            ))}
                                            {book.reRegistrationRisk.length > 8 && (
                                                <span style={{ fontSize: 9, color: '#A7B0BC', padding: '2px 4px' }}>
                                                    외 {book.reRegistrationRisk.length - 8}명
                                                </span>
                                            )}
                                        </div>
                                        <span
                                            style={{ fontSize: 8, color: '#9AA5B2', lineHeight: '11px', display: 'block', marginTop: 5 }}
                                        >
                                            {/*
                                              "10명 중 7명"도 68.3% 에서 나온 말이다.
                                              퍼센트만 상수로 바꾸고 이 문장을 두면, 상수가 갱신됐을 때
                                              한 문장 안에서 두 숫자가 서로 다른 말을 하게 된다.
                                            */}
                                            라포 전체에서 첫 수강권 회원 10명 중{' '}
                                            {Math.round(FIRST_PASS_CHURN_RATE / 10)}명은 두 번째로 이어지지
                                            않습니다 (이탈률 {FIRST_PASS_CHURN_RATE}%). 페이스가 꺾이기 전에
                                            손을 써야 합니다.
                                        </span>
                                    </div>
                                    )}
                            </div>

                            <div style={{ marginTop: 10 }}>
                                    <SectionTitle
                                        no={no()}
                                        title="곧 끝나는 회원"
                                        sub={`잔여 ${ENDING_SOON_SESSIONS}회 이하 또는 만료 ${ENDING_SOON_DAYS}일 이내 · 다음 달 영업 대상`}
                                    />
                                    {book.endingSoon.length === 0 ? (
                                        <div
                                            className="pdf-keep"
                                            style={{ border: `1px solid ${LINE}`, borderRadius: 3, padding: '9px 12px' }}
                                        >
                                            <span style={{ fontSize: 10, color: '#5B6B7F' }}>
                                                이번 달 안에 끝나는 회원이 없습니다.
                                            </span>
                                            <span style={{ fontSize: 8.5, color: '#9AA5B2', display: 'block', marginTop: 3, lineHeight: '12px' }}>
                                                담당 회원 {book.total}명 모두 잔여 {ENDING_SOON_SESSIONS}회를 넘고
                                                만료도 {ENDING_SOON_DAYS}일 이상 남았습니다. 다음 달 재등록 영업
                                                대상은 아직 없습니다.
                                            </span>
                                        </div>
                                    ) : (
                                    <div style={{ border: `1px solid ${LINE}`, borderRadius: 3, overflow: 'hidden' }}>
                                        <div
                                            className="flex items-center"
                                            style={{ background: SOFT, padding: '4px 9px', gap: 7 }}
                                        >
                                            <span style={{ fontSize: 7.5, fontWeight: 700, color: MUTED, width: 52, flex: 'none' }}>
                                                회원
                                            </span>
                                            <span style={{ fontSize: 7.5, fontWeight: 700, color: MUTED, flex: 1 }}>
                                                상품
                                            </span>
                                            <span style={{ fontSize: 7.5, fontWeight: 700, color: MUTED, width: 46, textAlign: 'right' }}>
                                                잔여
                                            </span>
                                            <span style={{ fontSize: 7.5, fontWeight: 700, color: MUTED, width: 46, textAlign: 'right' }}>
                                                만료
                                            </span>
                                            {/*
                                              🔴 「순번 평균」 칸을 뺐다 (2026-08-31 QA20, 대표 지시).
                                                 같은 순번이면 전원이 같은 값이라 회원 줄에 개인 수치처럼
                                                 서 있으면 안 된다 (MemberRow 주석 참고). 순번 자체는
                                                 상품 옆에 `passOrderLabel` 로 이미 적혀 있다.

                                              「걸린 금액」은 이제 **미사용 선수금**이다.
                                                 예전에는 재등록 기대액이 섞여 있어 각주로 "잔여분이 함께
                                                 들어 있다"고 변명해야 했다. 이제 한 가지 뜻만 갖는다.
                                            */}
                                            <span style={{ fontSize: 7.5, fontWeight: 700, color: MUTED, width: 62, textAlign: 'right' }}>
                                                걸린 금액
                                            </span>
                                        </div>
                                        {book.endingSoon.slice(0, endingLimit).map((m, i) => (
                                            <div
                                                key={m.memberId}
                                                className="pdf-keep flex items-center"
                                                style={{
                                                    padding: '4px 9px',
                                                    borderTop: i === 0 ? 'none' : `1px solid ${LINE}`,
                                                    gap: 7,
                                                }}
                                            >
                                                <span style={{ fontSize: 9.5, fontWeight: 700, width: 52, flex: 'none' }}>
                                                    {m.name}
                                                </span>
                                                <span style={{ fontSize: 8.5, color: '#5B6B7F', flex: 1, minWidth: 0 }}>
                                                    {m.product} · {m.passOrderLabel}
                                                </span>
                                                {/* 무제한(대관)은 등록상 999회다 — 회차로 찍지도, 급한 색을 주지도 않는다 */}
                                                <span
                                                    style={{
                                                        fontSize: 9,
                                                        fontWeight:
                                                            !m.sessionless && (m.remain ?? 99) <= 4 ? 800 : 400,
                                                        color:
                                                            !m.sessionless && (m.remain ?? 99) <= 4 ? BAD : MUTED,
                                                        width: 46,
                                                        textAlign: 'right',
                                                    }}
                                                >
                                                    {m.sessionless
                                                        ? '무제한'
                                                        : m.remain == null
                                                          ? '—'
                                                          : `${m.remain}회`}
                                                </span>
                                                {/*
                                                  잔여 회차로 걸려 들어온 회원은 만료일이 이미 지났을 수 있다.
                                                  예전에는 음수 분기가 없어 `D--5` 가 찍혔다.
                                                */}
                                                <span
                                                    style={{
                                                        fontSize: 9,
                                                        fontWeight:
                                                            isExpired(m.expiryDday) || (m.expiryDday ?? 99) <= 14
                                                                ? 800
                                                                : 400,
                                                        color: isExpired(m.expiryDday)
                                                            ? BAD
                                                            : (m.expiryDday ?? 99) <= 14
                                                              ? WARN
                                                              : MUTED,
                                                        width: 46,
                                                        textAlign: 'right',
                                                    }}
                                                >
                                                    {expiryText(m.expiryDday)}
                                                </span>
                                                <span
                                                    style={{
                                                        fontSize: 9,
                                                        fontWeight: m.prepaid == null ? 400 : 700,
                                                        color: m.prepaid == null ? '#9AA5B2' : INK,
                                                        width: 62,
                                                        textAlign: 'right',
                                                    }}
                                                >
                                                    {m.prepaid == null ? '산정 불가' : `${shortWon(m.prepaid)}원`}
                                                </span>
                                            </div>
                                        ))}
                                        {book.endingSoon.length > endingLimit && (
                                            <div style={{ padding: '4px 9px', borderTop: `1px solid ${LINE}`, background: '#FBFCFD' }}>
                                                <span style={{ fontSize: 8, color: '#9AA5B2' }}>
                                                    급한 순서로 {endingLimit}명만 실었습니다. 나머지{' '}
                                                    {book.endingSoon.length - endingLimit}명은 회원 현황에서
                                                    확인하세요.
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                    )}
                                    <span style={{ fontSize: 8, color: '#9AA5B2', marginTop: 4, display: 'block', lineHeight: '11px' }}>
                                        만료일만 보면 놓칩니다. 20회권을 사놓고 2회 남은 회원은 만료가 두 달
                                        남았어도 다음 주에 끝납니다. 기간과 회차를 함께 봅니다.
                                        {' '}D-숫자는 만료까지 남은 날, D+숫자는 만료된 뒤 지난 날입니다.
                                        {' '}&lsquo;걸린 금액&rsquo;은 <b>미사용 선수금</b>입니다 — 잔여 회차 ×
                                        회당 단가, 이미 받았지만 아직 수업하지 않은 금액입니다. 재등록 기대액은
                                        섞여 있지 않습니다(그쪽은 위 「다음 달 재등록 예상」이 따로 말합니다).
                                    </span>
                            </div>

                            <div style={{ marginTop: 10 }}>
                                {/*
                                  🔴 부제의 「칸 순서는 …」 산문을 뺐다 (2026-09-01 QA27).
                                     이제 열 위에 라벨이 서 있으므로 같은 말을 두 번 하는 셈이다.
                                     값이 무엇인지는 **그 값 바로 위**에서 읽혀야지, 표 밖의
                                     문장을 기억해서 눈으로 옮겨 세는 일이 되면 안 된다.
                                */}
                                <SectionTitle no={no()} title="회원별 상태" />
                                <div style={{ border: `1px solid ${LINE}`, borderRadius: 3, overflow: 'hidden' }}>
                                    <MemberTableHead />
                                    {memberBlocks.map((b) => (
                                        <StageBlock
                                            key={b.title}
                                            title={b.title}
                                            tone={b.tone}
                                            rows={b.rows}
                                            hint={b.hint}
                                            limit={b.limit}
                                        />
                                    ))}
                                    {memberHidden > 0 && (
                                        <div style={{ padding: '3px 9px', borderTop: `1px solid ${LINE}`, background: SOFT }}>
                                            <span style={{ fontSize: 7.5, color: MUTED }}>
                                                단계마다 급한 순서로 실었습니다. 나머지 {memberHidden}명은 회원 현황
                                                화면에서 확인하세요.
                                            </span>
                                        </div>
                                    )}
                                </div>
                                {/*
                                  표기 각주.
                                  인쇄물에는 마우스오버가 없습니다. 여기서 밝히지 않으면
                                  'D+39'·'평균 52.9%'·'산정 불가'가 무슨 뜻인지 종이 위에서는
                                  알 길이 없습니다.
                                */}
                                <span style={{ fontSize: 8, color: '#9AA5B2', marginTop: 4, display: 'block', lineHeight: '11px' }}>
                                    수업 속도는 최근 60일과 이전 60일의 수업 수를 견준 것입니다(주당 아님).
                                    {' '}{Math.abs(STABLE_RULE.paceDropPct)}% 넘게 줄면 &lsquo;느려짐&rsquo;,
                                    늘면 &lsquo;빨라짐&rsquo;, 그 사이는 &lsquo;유지&rsquo;입니다 — 화면의 페이스
                                    경고와 같은 선입니다. 원 수치는 목록 화면에서 볼 수 있습니다.
                                    {' '}D-숫자는 만료까지 남은 날, D+숫자는 만료된 뒤 지난 날입니다.
                                    {' '}&lsquo;걸린 금액&rsquo;은 미사용 선수금(잔여 회차 × 회당 단가)입니다.
                                    {' '}&lsquo;산정 불가&rsquo;는 단가와 결제 금액이 없는 수강권이라 금액을 계산하지
                                    않았다는 뜻이며, 0원이라는 뜻이 아닙니다.
                                </span>
                            </div>
                        </>
                    ) : (
                        <div
                            className="pdf-keep"
                            style={{ border: `1px solid ${LINE}`, borderRadius: 3, padding: '14px 16px' }}
                        >
                            <span style={{ fontSize: 10.5, color: '#5B6B7F' }}>
                                이 달에 배정된 담당 회원 기록이 없습니다.
                            </span>
                        </div>
                    )}

                    {hasClosing && closing && (
                        <div style={{ marginTop: 10 }}>
                            <SectionTitle no={no()} title="신규기회 클로징" sub="체험·OT에서 실제 결제까지" />
                            <div
                                className="flex"
                                style={{ border: `1px solid ${LINE}`, borderRadius: 3, padding: '11px 14px', gap: 14 }}
                            >
                                <BigStat
                                    label="배정받은 회원"
                                    value={String(closing.leadMembers)}
                                    unit="명"
                                    note={`기회 ${closing.opportunities}건`}
                                />
                                <BigStat label="노쇼" value={String(closing.noShow)} unit="건" note="약속 후 미방문" />
                                <BigStat
                                    label="결제 성사"
                                    value={String(closing.closed)}
                                    unit="명"
                                    note="실제 수강권 결제 기준"
                                    tone={GOOD}
                                />
                                <BigStat
                                    label="클로징률"
                                    value={closing.closingRate == null ? '—' : String(closing.closingRate)}
                                    unit={closing.closingRate == null ? undefined : '%'}
                                    note={avg.closingRate == null ? '기준 없음' : `센터 평균 ${avg.closingRate}%`}
                                />
                                <BigStat
                                    label="평균 성사 시점"
                                    value={closing.avgOrderToClose == null ? '—' : String(closing.avgOrderToClose)}
                                    unit={closing.avgOrderToClose == null ? undefined : '번째'}
                                    note="몇 번 만나고 결제까지 갔나"
                                />
                                <BigStat
                                    label="기회 일지 작성"
                                    value={closing.journalRate == null ? '—' : String(closing.journalRate)}
                                    unit={closing.journalRate == null ? undefined : '%'}
                                    note="체험·OT 후 기록 남긴 비율"
                                />
                            </div>

                            {/* 유형별 — 어느 유형에 강한지 보여야 무엇을 맡길지 정한다 */}
                            {closing.byType.length > 0 && (
                                <div style={{ marginTop: 7, border: `1px solid ${LINE}`, borderRadius: 3, overflow: 'hidden' }}>
                                    <div className="flex items-center" style={{ background: SOFT, padding: '4px 9px', gap: 7 }}>
                                        <span style={{ fontSize: 7.5, fontWeight: 700, color: MUTED, flex: 1 }}>
                                            유형
                                        </span>
                                        {['배정', '성사', '성사율'].map((h) => (
                                            <span
                                                key={h}
                                                style={{ fontSize: 7.5, fontWeight: 700, color: MUTED, width: 46, textAlign: 'right' }}
                                            >
                                                {h}
                                            </span>
                                        ))}
                                    </div>
                                    {closing.byType.map((t, i) => {
                                        const rate = t.leadMembers > 0
                                            ? Math.round((t.closed / t.leadMembers) * 1000) / 10
                                            : null;
                                        return (
                                            <div
                                                key={t.name}
                                                className="pdf-keep flex items-center"
                                                style={{ padding: '4px 9px', borderTop: i === 0 ? 'none' : `1px solid ${LINE}`, gap: 7 }}
                                            >
                                                <span style={{ fontSize: 9, fontWeight: 600, flex: 1 }}>{t.name}</span>
                                                <span style={{ fontSize: 9, color: MUTED, width: 46, textAlign: 'right' }}>
                                                    {t.leadMembers}명
                                                </span>
                                                <span style={{ fontSize: 9, color: MUTED, width: 46, textAlign: 'right' }}>
                                                    {t.closed}명
                                                </span>
                                                <span
                                                    style={{
                                                        fontSize: 9, fontWeight: 800, width: 46, textAlign: 'right',
                                                        color: rate == null ? '#9AA5B2' : rate >= 30 ? GOOD : rate < 15 ? BAD : INK,
                                                    }}
                                                >
                                                    {rate == null ? '—' : `${rate}%`}
                                                </span>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                            {closing.closedNames.length > 0 && (
                                <div
                                    className="pdf-keep"
                                    style={{
                                        marginTop: 6,
                                        background: '#F0F7F4',
                                        borderLeft: `3px solid ${GOOD}`,
                                        borderRadius: 3,
                                        padding: '7px 11px',
                                    }}
                                >
                                    <span style={{ fontSize: 8.5, color: '#5D9384' }}>결제로 이어진 회원</span>
                                    <p style={{ fontSize: 10, marginTop: 2, lineHeight: '14px' }}>
                                        {closing.closedNames.join(' · ')}
                                    </p>
                                </div>
                            )}
                        </div>
                    )}

                    <div style={{ marginTop: 10 }}>
                        <SectionTitle
                            no={no()}
                            title="센터 내 위치"
                            sub={`강사 ${(peers ?? [coach]).length}명 · 매출 순`}
                        />
                        <PeerTable me={coach} peers={peers ?? [coach]} limit={PEER_LIMIT} />
                        <span style={{ fontSize: 8, color: '#9AA5B2', marginTop: 4, display: 'block', lineHeight: '11px' }}>
                            같은 센터 안에서의 상대 위치입니다. 담당 회원 수와 배정 유형이 달라 단순 우열로 읽지
                            마시고, 대화의 출발점으로만 쓰세요.
                        </span>
                    </div>

                </Page>
                )}

                {/* ══════════ 마지막 장 · 면담과 합의 ══════════ */}
                <Page no={TOTAL} total={TOTAL} coachName={coach.name} monthLabel={monthLabel}>
                    <PageHead title="면담 기록과 다음 달 합의" sub={`${monthLabel} 정기 면담`} />

                    {record && record.total > 0 && (
                        <div>
                            {/*
                              ⚠️ 이 섹션은 강사를 평가하는 자리가 아니다.
                              2026-08-26 부터 강사 앱으로 푸시가 나가지만(OPS_ORDER_PUSH_ENABLED),
                              알림을 껐거나 기기가 등록되지 않은 강사에게는 닿지 않고, 그 결과를
                              웹은 알 수 없다. 받았는지 모르는 지시를 "미실행 3건, 실행률 62%" 로
                              적어 서명란까지 붙이면, 받은 적 없는 일로 평가받는 문서가 된다.
                              그래서 '미실행'이 아니라 '미확인'이고, '실행률'이 아니라 '확인율'이며,
                              무엇 기준인지를 섹션 머리에서 먼저 밝힌다.
                            */}
                            <SectionTitle
                                no={no()}
                                title="지시 이행"
                                sub="원장님이 남긴 지시와 그 처리 기록"
                            />
                            <div
                                className="pdf-keep"
                                style={{
                                    border: `1px solid ${LINE}`,
                                    borderLeft: `2px solid ${MUTED}`,
                                    borderRadius: 3,
                                    padding: '6px 10px',
                                    marginBottom: 5,
                                }}
                            >
                                <span style={{ fontSize: 8.5, color: MUTED, lineHeight: '12px' }}>
                                    강사 앱 알림은 아직 제공 전입니다. 아래는 원장님 기록 기준입니다 — 강사가
                                    지시를 전달받고도 처리하지 않았다는 뜻이 아닙니다.
                                    {/*
                                      🔴 상한에 걸린 달은 이행률을 단정하면 안 된다.
                                         목록이 sentAt 내림차순 200건까지만 오고, 그 상한은
                                         월 필터 뒤에도 남는다. 몇 건이 잘렸는지는 서버가
                                         총건수를 안 줘서 알 수 없다 — 밝히는 것이 유일한 정직이다.
                                    */}
                                    {record.capped && (
                                        <>
                                            {' '}
                                            <b>
                                                이 달 지시가 {ORDER_LIST_CAP}건 상한에 닿아 오래된 건이
                                                빠졌을 수 있습니다 — 확인율을 확정 수치로 읽지 마세요.
                                            </b>
                                        </>
                                    )}
                                </span>
                            </div>
                            <div
                                className="pdf-keep flex items-center"
                                style={{ gap: 14, border: `1px solid ${LINE}`, borderRadius: 3, padding: '11px 14px' }}
                            >
                                <div style={{ textAlign: 'center', flex: 'none', paddingRight: 14, borderRight: `1px solid ${LINE}` }}>
                                    <span
                                        style={{
                                            fontSize: 24,
                                            fontWeight: 800,
                                            letterSpacing: '-0.04em',
                                            color: (record.ratePct ?? 0) >= 80 ? GOOD : BAD,
                                        }}
                                    >
                                        {record.ratePct}%
                                    </span>
                                    {/* '실행률'이 아니라 '확인율' — 원장님 기록 기준임이 드러나야 한다 */}
                                    <span style={{ fontSize: 8, color: MUTED, display: 'block' }}>
                                        확인율 · {record.total}건 중 {record.done}건
                                    </span>
                                </div>
                                <div style={{ flex: 1 }}>
                                    {record.pending.length === 0 ? (
                                        <span style={{ fontSize: 10.5, color: GOOD, fontWeight: 700 }}>
                                            남긴 지시가 모두 처리됐습니다.
                                        </span>
                                    ) : (
                                        <>
                                            <span style={{ fontSize: 9, color: BAD, fontWeight: 700 }}>
                                                미확인 {record.pending.length}건
                                                {record.pendingValue > 0 && (
                                                    <span style={{ color: INK, fontWeight: 400 }}>
                                                        {' '}· 걸린 금액 {won(record.pendingValue)}원
                                                    </span>
                                                )}
                                            </span>
                                            <p style={{ fontSize: 9, color: MUTED, marginTop: 3, lineHeight: '13px' }}>
                                                가장 오래된 건은 {record.pending[0].memberName} 회원 ·{' '}
                                                {record.pending[0].daysAgo}일 경과입니다.
                                            </p>
                                        </>
                                    )}
                                </div>
                            </div>

                            {/* 지시 목록 — 무엇을 보냈고 무엇이 남았는지 한 장에 */}
                            <div style={{ marginTop: 7, border: `1px solid ${LINE}`, borderRadius: 3, overflow: 'hidden' }}>
                                <div className="flex items-center" style={{ background: SOFT, padding: '4px 9px', gap: 7 }}>
                                    <span style={{ fontSize: 7.5, fontWeight: 700, color: MUTED, width: 50, flex: 'none' }}>
                                        회원
                                    </span>
                                    <span style={{ fontSize: 7.5, fontWeight: 700, color: MUTED, flex: 1 }}>
                                        지시 내용
                                    </span>
                                    <span style={{ fontSize: 7.5, fontWeight: 700, color: MUTED, width: 54, textAlign: 'right' }}>
                                        걸린 금액
                                    </span>
                                    <span style={{ fontSize: 7.5, fontWeight: 700, color: MUTED, width: 44, textAlign: 'right' }}>
                                        경과
                                    </span>
                                    <span style={{ fontSize: 7.5, fontWeight: 700, color: MUTED, width: 34, textAlign: 'right' }}>
                                        상태
                                    </span>
                                </div>
                                {record.all.slice(0, ORDER_LIMIT).map((o, i) => (
                                    <div
                                        key={`${o.memberName}-${o.task}-${o.daysAgo}`}
                                        className="pdf-keep flex items-center"
                                        style={{
                                            padding: '4px 9px',
                                            borderTop: i === 0 ? 'none' : `1px solid ${LINE}`,
                                            gap: 7,
                                            background: o.done ? '#FFFFFF' : '#FFFCFC',
                                        }}
                                    >
                                        <span style={{ fontSize: 9, fontWeight: 700, width: 50, flex: 'none' }}>
                                            {o.memberName}
                                        </span>
                                        <span
                                            style={{
                                                fontSize: 8.5,
                                                color: '#5B6B7F',
                                                flex: 1,
                                                minWidth: 0,
                                                textDecoration: o.done ? 'line-through' : 'none',
                                            }}
                                        >
                                            {o.task}
                                        </span>
                                        <span style={{ fontSize: 8.5, color: MUTED, width: 54, textAlign: 'right' }}>
                                            {o.valueAmount > 0 ? shortWon(o.valueAmount) : '—'}
                                        </span>
                                        <span style={{ fontSize: 8.5, color: MUTED, width: 44, textAlign: 'right' }}>
                                            {o.daysAgo}일 전
                                        </span>
                                        <span
                                            style={{
                                                fontSize: 8.5, fontWeight: 800, width: 34, textAlign: 'right',
                                                color: o.done ? GOOD : BAD,
                                            }}
                                        >
                                            {o.done ? '확인' : '미확인'}
                                        </span>
                                    </div>
                                ))}
                                {record.all.length > ORDER_LIMIT && (
                                    <div style={{ padding: '4px 9px', borderTop: `1px solid ${LINE}`, background: '#FBFCFD' }}>
                                        <span style={{ fontSize: 8, color: '#9AA5B2' }}>
                                            미확인 우선 {ORDER_LIMIT}건만 실었습니다. 나머지{' '}
                                            {record.all.length - ORDER_LIMIT}건은 지시 및 진행 현황에서 확인하세요.
                                        </span>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    <div style={{ marginTop: 10 }}>
                        <SectionTitle no={no()} title="면담에서 나눌 이야기" sub="그대로 읽으셔도 됩니다" />
                        {plan.talkingPoints.slice(0, TALK_LIMIT).map((p, i) => (
                            <TalkCard key={p.headline} p={p} i={i} />
                        ))}
                    </div>

                    <div style={{ marginTop: 11 }}>
                        <SectionTitle no={no()} title="다음 달 목표" sub="함께 정하고 체크합니다" />
                        <div className="flex flex-col" style={{ gap: 5 }}>
                            {plan.goals.map((g, i) => (
                                <div
                                    key={g.label}
                                    className="pdf-keep flex items-start"
                                    style={{ gap: 8, border: `1px solid ${LINE}`, borderRadius: 3, padding: '7px 11px' }}
                                >
                                    <span
                                        style={{
                                            fontSize: 7.5,
                                            fontWeight: 800,
                                            color: '#FFFFFF',
                                            background: NAVY,
                                            borderRadius: 2,
                                            padding: '2px 4px',
                                            flex: 'none',
                                            marginTop: 1,
                                        }}
                                    >
                                        {i + 1}
                                    </span>
                                    <div style={{ flex: 1 }}>
                                        <span style={{ fontSize: 10.5, fontWeight: 700, lineHeight: '15px' }}>
                                            {g.label}
                                        </span>
                                        <span
                                            style={{ fontSize: 8.5, color: MUTED, display: 'block', marginTop: 2, lineHeight: '12px' }}
                                        >
                                            왜 · {g.why}
                                        </span>
                                    </div>
                                    <span
                                        style={{ width: 12, height: 12, border: `1.5px solid ${LINE}`, borderRadius: 2, flex: 'none', marginTop: 2 }}
                                    />
                                </div>
                            ))}
                        </div>
                    </div>

                    {plan.observationPlan.length > 0 && plan.maturity.level !== 'RICH' && (
                        <div style={{ marginTop: 11 }}>
                            <SectionTitle no={no()} title="다음 달에 볼 수 있게 되는 것" sub="지금은 표본이 부족합니다" />
                            <div style={{ border: `1px solid ${LINE}`, borderRadius: 3, overflow: 'hidden' }}>
                                {plan.observationPlan.map((o, i) => (
                                    <div
                                        key={o.what}
                                        className="pdf-keep flex items-start"
                                        style={{ padding: '6px 10px', borderTop: i === 0 ? 'none' : `1px solid ${LINE}`, gap: 9 }}
                                    >
                                        <span style={{ fontSize: 9.5, fontWeight: 700, width: 92, flex: 'none' }}>
                                            {o.what}
                                        </span>
                                        <span style={{ fontSize: 9, color: '#5B6B7F', flex: 1, lineHeight: '13px' }}>
                                            {o.why} {o.needs}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    <div style={{ marginTop: 11 }}>
                        <SectionTitle no={no()} title="면담 기록 및 확인" />
                        <div className="pdf-keep" style={{ border: `1px solid ${LINE}`, borderRadius: 3, padding: '11px 13px' }}>
                            <span style={{ fontSize: 8.5, color: MUTED }}>강사 의견 / 요청사항</span>
                            <div style={{ marginTop: 6 }}>
                                {[0, 1, 2, 3].map((i) => (
                                    <div key={i} style={{ borderBottom: `1px solid ${LINE}`, height: 19 }} />
                                ))}
                            </div>
                            <span style={{ fontSize: 8.5, color: MUTED, marginTop: 10, display: 'block' }}>
                                원장 메모 · 합의 사항
                            </span>
                            <div style={{ marginTop: 6 }}>
                                {[0, 1, 2].map((i) => (
                                    <div key={i} style={{ borderBottom: `1px solid ${LINE}`, height: 19 }} />
                                ))}
                            </div>
                            <div className="flex" style={{ gap: 22, marginTop: 13, alignItems: 'flex-end' }}>
                                <div style={{ width: 92, flex: 'none' }}>
                                    <span style={{ fontSize: 8.5, color: MUTED }}>면담일</span>
                                    <div style={{ borderBottom: `1px solid ${INK}`, height: 22 }} />
                                </div>
                                <div style={{ flex: 1 }}>
                                    <span style={{ fontSize: 8.5, color: MUTED }}>강사 서명</span>
                                    <div style={{ borderBottom: `1px solid ${INK}`, height: 22 }} />
                                </div>
                                <div style={{ flex: 1 }}>
                                    <span style={{ fontSize: 8.5, color: MUTED }}>원장 서명</span>
                                    <div style={{ borderBottom: `1px solid ${INK}`, height: 22 }} />
                                </div>
                            </div>
                        </div>
                    </div>

                    {plan.omitted.length > 0 && (
                        <div style={{ marginTop: 9 }}>
                            <span style={{ fontSize: 8, fontWeight: 700, color: MUTED }}>
                                이 리포트에 넣지 않은 것
                            </span>
                            {plan.omitted.map((o) => (
                                <span key={o} style={{ fontSize: 8, color: '#9AA5B2', display: 'block', lineHeight: '12px' }}>
                                    · {o}
                                </span>
                            ))}
                        </div>
                    )}
                </Page>
            </div>
        </div>
    );

    /*
     * body 로 포털을 씁니다.
     * 리포트를 여는 팝업이 position:fixed 라, 그 안에 있으면 인쇄할 때
     * 고정 요소로 취급돼 첫 장만 나옵니다.
     */
    const host = document.getElementById('coach-pdf-portal') ?? createPortalHost();
    return createPortal(body, host);
};

/** 인쇄 CSS 가 이 id 로 대상을 고른다 */
function createPortalHost(): HTMLElement {
    const el = document.createElement('div');
    el.id = 'coach-pdf-portal';
    document.body.appendChild(el);
    return el;
}

export default CoachPdfReport;
